import React, { useState, useEffect } from 'react';
import { X, Package, ShoppingCart, DollarSign, TrendingUp, BarChart3, Upload, Plus, Edit2, Trash2, Sparkles, Wallet, CreditCard, ArrowUpRight, ArrowDownRight, ArrowRight, PiggyBank, TrendingDown, SquareStar, Search, RefreshCw, Check, Loader, Download, History, ClipboardList, ChartColumn, CircleGauge, Activity, FileAxis3d, FileChartLine, Truck, ClipboardPlus, Users, Eye, Handshake, Calculator, PieChart, AlertTriangle, Target, Zap, TrendingDown as TrendingDownIcon } from 'lucide-react';
import { CATEGORY_COLORS, getCategoryColor } from '../config/categoryColors';
import './Dashboard.css';
import './RefreshOverlay.css';
import './AddProductForm.css';
import './PurchaseTab.css';
import './ReportesTab.css';
import ProductSearch from './ProductSearch';
import OrderDatePicker from './OrderDatePicker';
import SupplierModal from './SupplierModal';
import PurchaseModal from './PurchaseModal';
import NotificationSystem, { showSuccess, showError, showWarning, showInfo } from './NotificationSystem';
import apiService from '../services/api';
import purchaseApi from '../services/purchaseApi';

const Dashboard = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [categoryPeriod, setCategoryPeriod] = useState('today');
  const [productsPeriod, setProductsPeriod] = useState('today');
  const [financesPeriod, setFinancesPeriod] = useState('today');
  
  // State for real data from backend
  const [dashboardStats, setDashboardStats] = useState(null);
  const [salesByCategoryData, setSalesByCategoryData] = useState([]);
  const [topProductsData, setTopProductsData] = useState(null);
  const [todayTopProductsData, setTodayTopProductsData] = useState(null);
  const [paymentMethodsData, setPaymentMethodsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Indicadores tab — V2 data sources (same endpoints as HorizontePyME panel)
  const [dailySalesData, setDailySalesData] = useState(null);       // "Ventas hoy"
  const [averageInvoiceData, setAverageInvoiceData] = useState(null); // "Ticket Promedio"
  const [bestSellersData, setBestSellersData] = useState([]);        // "Más Vendido Hoy"
  const [topProductsV2Data, setTopProductsV2Data] = useState([]);    // "Top 5 Ventas"
  const [categories, setCategories] = useState([]);

  // State for products management
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // State for inline editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [ordersDateFilter, setOrdersDateFilter] = useState('');
  const [ordersStatusFilter, setOrdersStatusFilter] = useState('');
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [ordersTotalCount, setOrdersTotalCount] = useState(0);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableDatesLoading, setAvailableDatesLoading] = useState(false);
  const [purchaseAvailableDates, setPurchaseAvailableDates] = useState([]);
  
  // State for adding new products
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    price: '',
    category: 'Pan',
    description: '',
    stock: '10'
  });
  const [isCreating, setIsCreating] = useState(false);

  // State for Excel import
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showImportResults, setShowImportResults] = useState(false);

  // Purchase Management State
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchaseModalLoading, setPurchaseModalLoading] = useState(false);
  const [supplierModalLoading, setSupplierModalLoading] = useState(false);
  
  // Purchase Filters
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState('');
  const [purchaseDateFilter, setPurchaseDateFilter] = useState('');
  const [purchaseSupplierFilter, setPurchaseSupplierFilter] = useState('');
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchaseTotalPages, setPurchaseTotalPages] = useState(1);
  const [purchaseTotalCount, setPurchaseTotalCount] = useState(0);

  // Status Update Modal State
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedPurchaseForStatus, setSelectedPurchaseForStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [statusModalLoading, setStatusModalLoading] = useState(false);

  // Purchase Download Modal State
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadSupplier, setDownloadSupplier] = useState('');
  const [downloadDateRange, setDownloadDateRange] = useState('month');
  const [downloadLoading, setDownloadLoading] = useState(false);
  
  // Sales Download Modal State
  const [showSalesDownloadModal, setShowSalesDownloadModal] = useState(false);
  const [salesDownloadDateRange, setSalesDownloadDateRange] = useState('month');
  const [salesDownloadLoading, setSalesDownloadLoading] = useState(false);
  
  // Date Range Availability State
  const [dateRangeAvailability, setDateRangeAvailability] = useState({
    all: {
      day: true,
      week: true,
      month: true,
      threemonths: true
    },
    bySupplier: {}
  });
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  
  // Sales Availability State
  const [salesAvailability, setSalesAvailability] = useState({
    day: true,
    week: true,
    month: true,
    threemonths: true
  });
  const [salesAvailabilityLoading, setSalesAvailabilityLoading] = useState(false);

  // Analytics State (Phase 4.1)
  const [analyticsData, setAnalyticsData] = useState({
    cogsAnalysis: null,
    supplierPerformance: null,
    dailyProfitAnalysis: null,
    inventoryTurnover: null,
    businessHealthScore: null
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedAnalyticsType, setSelectedAnalyticsType] = useState(null);

  // Payment Methods Modal State
  const [showPaymentMethodsModal, setShowPaymentMethodsModal] = useState(false);
  const [paymentMethodsModalData, setPaymentMethodsModalData] = useState([]);
  const [paymentMethodsModalLoading, setPaymentMethodsModalLoading] = useState(false);
  const [paymentMethodsPeriod, setPaymentMethodsPeriod] = useState('today');

  // Real Analytics Data State
  const [analyticsMetrics, setAnalyticsMetrics] = useState({
    suppliersCount: 0,
    paymentMethodsCount: 0,
    averageMargin: 0,
    wastePercentage: 0,
    businessHealthScore: 0,
    dailyProfit: 0
  });

  // Fetch dashboard data from backend
  useEffect(() => {
    fetchDashboardData();
    fetchSalesByCategory(categoryPeriod);
    fetchTopProducts(productsPeriod);
    fetchTodayTopProducts(); // For the best seller stat card
    fetchPaymentMethods(financesPeriod);
    fetchCategories(); // Fetch categories for add product form
    fetchOrders(); // Load initial orders
    fetchAvailableDates(); // Load available dates for date picker
    fetchAnalyticsMetrics(); // Fetch real analytics data
    // Indicadores V2 data (matches HorizontePyME panel endpoints)
    fetchDailySalesProgress();
    fetchAverageInvoiceValue();
    fetchBestSellersToday();
    fetchTopProductsV2(productsPeriod);
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('Connection restored, refetching data...');
      fetchDashboardData();
      fetchSalesByCategory(categoryPeriod);
      fetchTopProducts(productsPeriod);
      fetchTodayTopProducts();
      fetchPaymentMethods(financesPeriod);
      fetchDailySalesProgress();
      fetchAverageInvoiceValue();
      fetchBestSellersToday();
      fetchTopProductsV2(productsPeriod);
    };

    const handleOffline = () => {
      console.log('Connection lost - entering offline mode');
      setError('Modo sin conexión - Los datos se mostrarán cuando se restablezca la conexión');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [categoryPeriod, productsPeriod, financesPeriod]);

  // Refetch sales by category when period changes
  useEffect(() => {
    fetchSalesByCategory(categoryPeriod);
  }, [categoryPeriod]);

  // Refetch top products when period changes
  useEffect(() => {
    fetchTopProducts(productsPeriod);
    fetchTopProductsV2(productsPeriod);
  }, [productsPeriod]);

  // Refetch payment methods when period changes
  useEffect(() => {
    fetchPaymentMethods(financesPeriod);
  }, [financesPeriod]);

  // Search products function
  const searchProducts = async (query) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await apiService.getProducts({ 
        search: query.trim(),
        active: true 
      });
      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm.trim()) {
        searchProducts(searchTerm);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle product selection from search results
  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Excel export function
  const handleExportExcel = async () => {
    try {
      console.log('Starting Excel export...');
      
      // Create download link and trigger download
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/products/export/excel`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to export Excel file');
      }

      // Get the filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log('Content-Disposition header:', contentDisposition);
      
      let filename = 'PRODUCTOS_export.xlsx';
      if (contentDisposition) {
        // Try different patterns to extract filename
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          console.log('Extracted filename:', filename);
        }
      }
      
      // Fallback: Generate timestamp-based filename if header parsing failed
      if (filename === 'PRODUCTOS_export.xlsx' || !filename.includes('PRODUCTOS_')) {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        const timestamp = `${hours}:${minutes}_${day}_${month}_${year}`;
        filename = `PRODUCTOS_${timestamp}.xlsx`;
        console.log('Using fallback filename:', filename);
      }

      // Convert response to blob and create download link
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Create temporary link element and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      console.log(`Excel file downloaded: ${filename}`);
      
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Error al exportar Excel. Por favor, inténtelo de nuevo.');
    }
  };

  // Excel import function
  const handleImportExcel = async (file) => {
    if (!file) return;

    setIsImporting(true);
    setImportResults(null);
    setShowImportResults(false);

    try {
      console.log('Starting Excel import...');
      
      const formData = new FormData();
      formData.append('excel', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/products/import/excel`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to import Excel file');
      }

      console.log('Import completed:', result);
      setImportResults(result.results);
      setShowImportResults(true);

      // Refresh dashboard data after successful import
      if (result.results.successful > 0) {
        await fetchDashboardData();
      }
      
    } catch (error) {
      console.error('Error importing Excel:', error);
      alert(`Error al importar Excel: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Handle file selection for import
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type) && 
          !file.name.toLowerCase().endsWith('.xlsx') && 
          !file.name.toLowerCase().endsWith('.xls')) {
        alert('Por favor, seleccione un archivo Excel válido (.xlsx o .xls)');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo permitido: 10MB');
        return;
      }

      handleImportExcel(file);
    }
    
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  };

  // Handle template download
  const handleDownloadTemplate = async () => {
    try {
      console.log('Downloading Excel template...');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/products/template/excel`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      // Get the filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'PRODUCTOS_TEMPLATE.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Convert response to blob and create download link
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      console.log(`Template downloaded: ${filename}`);
      
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error al descargar plantilla. Por favor, inténtelo de nuevo.');
    }
  };

  // Clear selected product
  const clearSelectedProduct = () => {
    setSelectedProduct(null);
  };

  // Inline editing functions
  const handleEditProduct = (product) => {
    setEditingProduct({ ...product });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingProduct(null);
  };

  const handleSaveEdit = async () => {
    if (!editingProduct) return;

    try {
      setIsSaving(true);
      
      // Update product
      const updatedProduct = await apiService.updateProduct(editingProduct.id, {
        name: editingProduct.name,
        price: parseFloat(editingProduct.price),
        category: editingProduct.category
      });

      // Update stock
      await apiService.updateStock(editingProduct.id, {
        quantity: parseInt(editingProduct.stock?.quantity || 0)
      });

      // Clear editing state first
      setIsEditing(false);
      setEditingProduct(null);
      
      // Show refresh indicator and refresh the selected product with updated data
      setIsRefreshing(true);
      setTimeout(async () => {
        try {
          const refreshedProduct = await apiService.getProduct(editingProduct.id);
          setSelectedProduct(refreshedProduct);
          console.log('Product refreshed with updated data:', refreshedProduct);
          
          // Show success indicator briefly
          setRefreshSuccess(true);
          setTimeout(() => setRefreshSuccess(false), 2000);
          
        } catch (refreshError) {
          console.error('Error refreshing product data:', refreshError);
          // Fallback: at least update with what we have
          setSelectedProduct(updatedProduct);
        } finally {
          setIsRefreshing(false);
        }
      }, 500); // 500ms delay to ensure backend has processed changes
      
      console.log('Product updated successfully');
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error al actualizar el producto: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este producto? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setIsDeleting(true);
      
      await apiService.deleteProduct(productId);
      
      // Clear selected product if it was the one deleted
      if (selectedProduct?.id === productId) {
        setSelectedProduct(null);
      }
      
      console.log('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error al eliminar el producto: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditingProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (!selectedProduct || isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      const refreshedProduct = await apiService.getProduct(selectedProduct.id);
      setSelectedProduct(refreshedProduct);
      console.log('Product manually refreshed:', refreshedProduct);
      
      // Show success indicator briefly
      setRefreshSuccess(true);
      setTimeout(() => setRefreshSuccess(false), 2000);
      
    } catch (error) {
      console.error('Error manually refreshing product:', error);
      alert('Error al actualizar los datos: ' + error.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Add product functions
  const handleAddProduct = () => {
    setIsAddingProduct(true);
    setNewProduct({
      name: '',
      price: '',
      category: categories.length > 0 ? categories[0] : 'Pan',
      description: '',
      stock: '10'
    });
  };

  const handleCancelAdd = () => {
    setIsAddingProduct(false);
    setNewProduct({
      name: '',
      price: '',
      category: categories.length > 0 ? categories[0] : 'Pan',
      description: '',
      stock: '10'
    });
  };

  const handleNewProductChange = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      alert('Por favor completa al menos el nombre y precio del producto');
      return;
    }

    try {
      setIsCreating(true);
      
      // Create the product
      const createdProduct = await apiService.createProduct({
        name: newProduct.name,
        price: parseFloat(newProduct.price),
        category: newProduct.category,
        description: newProduct.description || null
      });

      // Create initial stock entry
      if (newProduct.stock) {
        await apiService.updateStock(createdProduct.id, {
          quantity: parseInt(newProduct.stock)
        });
      }

      // Refresh the created product to get complete data
      setTimeout(async () => {
        try {
          const refreshedProduct = await apiService.getProduct(createdProduct.id);
          setSelectedProduct(refreshedProduct);
          
          // Clear add form
          setIsAddingProduct(false);
          setNewProduct({
            name: '',
            price: '',
            category: 'Pan',
            description: '',
            stock: '10'
          });
          
          // Show success
          setRefreshSuccess(true);
          setTimeout(() => setRefreshSuccess(false), 2000);
          
          console.log('Product created successfully:', refreshedProduct);
        } catch (refreshError) {
          console.error('Error refreshing created product:', refreshError);
          setSelectedProduct(createdProduct);
        }
      }, 500);

    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error al crear el producto: ' + error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Check if we're online
      if (!navigator.onLine) {
        console.log('Offline mode detected, using fallback data');
        setDashboardStats({
          summary: {
            totalRevenue: 0,
            totalOrders: 0,
            averageOrderValue: 0
          },
          paymentMethods: [],
          topProducts: []
        });
        setError(null);
        return;
      }
      
      const stats = await apiService.getDashboardStats();
      console.log('Dashboard stats received:', stats);
      setDashboardStats(stats);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      
      // Check if it's a network error
      if (err.name === 'TypeError' || err.message.includes('fetch') || err.message.includes('NetworkError')) {
        console.log('Network error detected, using offline fallback');
        setDashboardStats({
          summary: {
            totalRevenue: 0,
            totalOrders: 0,
            averageOrderValue: 0
          },
          paymentMethods: [],
          topProducts: []
        });
        setError('Modo sin conexión - Los datos se mostrarán cuando se restablezca la conexión');
      } else {
        setError('Error al cargar los datos del dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesData = await apiService.getCategories();
      console.log('Categories received:', categoriesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Set default categories as fallback
      setCategories([
        'Pan', 'Facturas', 'Dulces', 'Salados', 'Aptos', 
        'Heladera', 'Congelados', 'Infusiones', 'Regalos', 'Almacen'
      ]);
    }
  };

  const fetchSalesByCategory = async (period) => {
    try {
      if (!navigator.onLine) {
        console.log('Offline mode - skipping sales by category fetch');
        setSalesByCategoryData([]);
        return;
      }
      
      const filters = getDateFilters(period);
      // v2 endpoint expects YYYY-MM-DD strings (matches HorizontePyME SalesByCategory)
      const startDate = (filters.startDate || new Date().toISOString()).split('T')[0];
      const endDate = (filters.endDate || new Date().toISOString()).split('T')[0];
      const data = await apiService.getSalesByCategoryV2(startDate, endDate);
      console.log(`Sales by category v2 (${period}):`, data);
      setSalesByCategoryData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching sales by category:', err);
      setSalesByCategoryData([]);
    }
  };

  const fetchTopProducts = async (period) => {
    try {
      const filters = getDateFilters(period);
      const stats = await apiService.getDashboardStats(filters);
      console.log(`Top products (${period}):`, stats.topProducts);
      setTopProductsData(stats.topProducts || []);
    } catch (err) {
      console.error('Error fetching top products:', err);
    }
  };

  const fetchTodayTopProducts = async () => {
    try {
      const filters = getDateFilters('today');
      const stats = await apiService.getDashboardStats(filters);
      console.log('Today top products for best seller:', stats.topProducts);
      setTodayTopProductsData(stats.topProducts || []);
    } catch (err) {
      console.error('Error fetching today top products:', err);
    }
  };

  // ------------------------------------------------------------------
  // Indicadores V2 fetchers — same endpoints as HorizontePyME panel
  // ------------------------------------------------------------------

  // "Ventas hoy" → /analytics/v2/daily-sales-progress (data.current.sales)
  const fetchDailySalesProgress = async () => {
    try {
      if (!navigator.onLine) return;
      const response = await apiService.getDailySalesProgress();
      if (response?.success && response?.data) {
        setDailySalesData(response.data);
      }
    } catch (err) {
      console.error('Error fetching daily sales progress:', err);
    }
  };

  // "Ticket Promedio" → /analytics/average-invoice-value?date=YYYY-MM-DD (data.AverageInvoiceValue)
  const fetchAverageInvoiceValue = async () => {
    try {
      if (!navigator.onLine) return;
      const today = new Date().toISOString().split('T')[0];
      const response = await apiService.getAverageInvoiceValue(today);
      if (response?.data) {
        setAverageInvoiceData(response.data);
      }
    } catch (err) {
      console.error('Error fetching average invoice value:', err);
    }
  };

  // "Más Vendido Hoy" → /analytics/best-sellers?count=10&period=day (data[0])
  const fetchBestSellersToday = async () => {
    try {
      if (!navigator.onLine) return;
      const response = await apiService.getBestSellers(10, 'day');
      setBestSellersData(response?.data || []);
    } catch (err) {
      console.error('Error fetching best sellers today:', err);
    }
  };

  // "Top 5 Ventas" → /analytics/top-products?period=day&count=5
  const fetchTopProductsV2 = async (period) => {
    try {
      if (!navigator.onLine) return;
      // CassaPyME uses 'today' where HorizontePyME uses 'day'
      const mappedPeriod = period === 'today' ? 'day' : period;
      const response = await apiService.getTopProducts(mappedPeriod, 5);
      setTopProductsV2Data(response?.data || []);
    } catch (err) {
      console.error('Error fetching top products v2:', err);
    }
  };

  const fetchPaymentMethods = async (period) => {    try {
      const filters = getDateFilters(period);
      const stats = await apiService.getDashboardStats(filters);
      console.log(`Payment methods (${period}):`, stats.paymentMethods);
      setPaymentMethodsData(stats.paymentMethods || []);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  };

  const fetchAnalyticsMetrics = async () => {
    try {
      if (!navigator.onLine) {
        console.log('Offline mode - using default analytics metrics');
        return;
      }
      
      console.log('Fetching analytics metrics...');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/analytics/metrics`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics metrics');
      }
      
      const data = await response.json();
      console.log('Analytics metrics received:', data);
      
      setAnalyticsMetrics({
        suppliersCount: data.suppliersCount || 0,
        paymentMethodsCount: data.paymentMethodsCount || 0,
        averageMargin: data.averageMargin || 0,
        wastePercentage: data.wastePercentage || 0,
        businessHealthScore: data.businessHealthScore || 0,
        dailyProfit: data.dailyProfit || 0
      });
    } catch (err) {
      console.error('Error fetching analytics metrics:', err);
      // Keep default values on error
    }
  };

  // Helper: Get date filters for periods
  const getDateFilters = (period) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (period) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: new Date().toISOString()
        };
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return {
          startDate: weekAgo.toISOString(),
          endDate: new Date().toISOString()
        };
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setDate(monthAgo.getDate() - 30);
        return {
          startDate: monthAgo.toISOString(),
          endDate: new Date().toISOString()
        };
      default:
        return {};
    }
  };

  // Fetch available dates that have orders
  const fetchAvailableDates = async () => {
    try {
      setAvailableDatesLoading(true);
      console.log('Fetching available dates...');
      const data = await apiService.getAvailableDates();
      
      console.log('Available dates received:', data);
      setAvailableDates(data.dates.map(item => item.date));
      console.log('Available dates set:', data.dates.map(item => item.date));
    } catch (error) {
      console.error('Error fetching available dates:', error);
      setAvailableDates([]);
    } finally {
      setAvailableDatesLoading(false);
    }
  };

  const fetchPurchaseAvailableDates = async () => {
    try {
      console.log('Fetching purchase available dates...');
      const response = await purchaseApi.purchases.getAll();
      
      if (response.success && response.data) {
        // Extract unique dates from purchase orders
        const uniqueDates = [...new Set(
          response.data.map(purchase => {
            const date = new Date(purchase.orderDate);
            return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          })
        )].sort();
        
        setPurchaseAvailableDates(uniqueDates);
        console.log('Purchase available dates set:', uniqueDates);
      }
    } catch (error) {
      console.error('Error fetching purchase available dates:', error);
      setPurchaseAvailableDates([]);
    }
  };

  // Fetch orders with optional filters
  const fetchOrders = async (page = 1, limit = 10) => {
    try {
      setOrdersLoading(true);

      const params = { page, limit };

      // Single-day date filter → send as dateFrom + dateTo (same day)
      if (ordersDateFilter) {
        params.dateFrom = ordersDateFilter;
        params.dateTo   = ordersDateFilter;
      }

      if (ordersStatusFilter && ordersStatusFilter !== '') {
        params.status = ordersStatusFilter;
      }

      const data = await apiService.getOrders(params);
      console.log('Invoices received:', data);

      // Response: { success, data: { invoices, pagination, summary } }
      if (data && data.success && data.data) {
        const { invoices, pagination } = data.data;
        setOrders(invoices || []);
        setOrdersPage(pagination?.page || 1);
        setOrdersTotalPages(pagination?.totalPages || 1);
        setOrdersTotalCount(pagination?.total || 0);
      } else {
        setOrders([]);
        setOrdersPage(1);
        setOrdersTotalPages(1);
        setOrdersTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setOrders([]);
      setOrdersPage(1);
      setOrdersTotalPages(1);
      setOrdersTotalCount(0);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Invoice status → display label + badge colour
  const getStatusColor = (status) => {
    const map = {
      'PRESUPUESTO': '#3498db',
      'approved':    '#27ae60',
      'completed':   '#27ae60',
      'pending':     '#f39c12',
      'processing':  '#f39c12',
      'draft':       '#e67e22',
      'error':       '#e74c3c',
      'failed':      '#e74c3c',
      'canceled':    '#e74c3c',
      'cancelled':   '#e74c3c',
    };
    return map[status] || '#95a5a6';
  };

  const getStatusLabel = (status) => {
    const map = {
      'PRESUPUESTO': 'Factura X',
      'approved':    'Aprobada',
      'completed':   'Aprobada',
      'pending':     'Pendiente',
      'processing':  'Procesando',
      'draft':       'En Cola',
      'error':       'Error',
      'failed':      'Fallida',
      'canceled':    'Cancelada',
      'cancelled':   'Cancelada',
    };
    return map[status] || status;
  };

  const getPaymentMethodLabel = (code) => {
    const map = {
      '201': 'Efectivo',
      '202': 'T. Débito',
      '203': 'T. Crédito',
      '204': 'Cta. Cte.',
      '205': 'Cheque',
      '206': 'Transferencia',
      '207': 'QR / Otros',
      '208': 'Otra',
    };
    return map[String(code)] || code || '-';
  };

  const getTipoLabel = (tipoComprobante, status) => {
    if (!tipoComprobante && status === 'PRESUPUESTO') return 'Factura X';
    if (!tipoComprobante) return '-';
    if (tipoComprobante.includes('NOTA DE CREDITO') || tipoComprobante.includes('NC')) return 'Nota de Crédito';
    if (tipoComprobante === 'PRESUPUESTO') return 'Factura X';
    if (tipoComprobante.includes('FACTURA A') || tipoComprobante === 'A') return 'Factura A';
    if (tipoComprobante.includes('FACTURA B') || tipoComprobante === 'B') return 'Factura B';
    if (tipoComprobante.includes('FACTURA C') || tipoComprobante === 'C') return 'Factura C';
    return tipoComprobante;
  };

  // Format order details summary (legacy – kept for backward compat)
  const formatOrderSummary = (orderItems) => {
    if (!orderItems || orderItems.length === 0) return 'Sin productos';
    return orderItems.map(item => `${item.productName} x${item.quantity}`).join(', ');
  };

  // Format invoice date: HH:MM DD/MM/YY (time from created_at, date from fecha_emision)
  const formatOrderDate = (fechaEmision, createdAt) => {
    if (!fechaEmision) return '-';
    const dateOnly = String(fechaEmision).split('T')[0];
    const [year, month, day] = dateOnly.split('-');
    const datePart = `${day}/${month}/${String(year).slice(-2)}`;
    if (createdAt) {
      const d = new Date(createdAt);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm} ${datePart}`;
    }
    return datePart;
  };

  // Handle order row click
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  // Close order detail
  const closeOrderDetail = () => {
    setShowOrderDetail(false);
    setSelectedOrder(null);
  };

  // Handle filter changes
  const handleDateFilterChange = (date) => {
    setOrdersDateFilter(date);
    setOrdersPage(1); // Reset to first page
    // Fetch will be triggered by useEffect
  };

  const handleStatusFilterChange = (status) => {
    setOrdersStatusFilter(status);
    setOrdersPage(1); // Reset to first page
    // Fetch will be triggered by useEffect
  };

  const handlePageChange = (newPage) => {
    setOrdersPage(newPage);
    // Fetch will be triggered by useEffect
  };

  // Fetch orders when filters or page changes
  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders(ordersPage);
    }
  }, [ordersDateFilter, ordersStatusFilter, ordersPage, activeTab]);

  // Purchase Management Functions
  const fetchPurchases = async (page = 1, limit = 5) => {
    try {
      setPurchasesLoading(true);
      const filters = {
        status: purchaseStatusFilter,
        supplierId: purchaseSupplierFilter,
        page,
        limit
      };
      
      // Convert single date to dateFrom/dateTo range for that day
      if (purchaseDateFilter) {
        const selectedDate = new Date(purchaseDateFilter);
        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        
        filters.dateFrom = selectedDate.toISOString().split('T')[0];
        filters.dateTo = nextDay.toISOString().split('T')[0];
      }
      
      console.log('Fetching purchases with filters:', filters);
      const response = await purchaseApi.purchases.getAll(filters);
      console.log('Purchase API response:', response);
      
      if (response.success) {
        setPurchases(response.data || []);
        setPurchaseTotalPages(response.pagination?.totalPages || 1);
        setPurchaseTotalCount(response.pagination?.totalCount || 0);
      } else {
        throw new Error(response.message || 'Error loading purchases');
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      
      // Only show error notification if it's not a network connectivity issue
      if (error.code !== 'ECONNREFUSED') {
        showError(
          'Error al Cargar Compras',
          'No se pudieron cargar las órdenes de compra. Verifique su conexión e intente nuevamente.'
        );
      }
      
      // Set empty state
      setPurchases([]);
      setPurchaseTotalPages(1);
      setPurchaseTotalCount(0);
    } finally {
      setPurchasesLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await purchaseApi.suppliers.getAll({ limit: 100 });
      
      if (response.success) {
        setSuppliers(response.data || []);
      } else {
        throw new Error(response.message || 'Error loading suppliers');
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      
      // Only show error notification if it's not a network connectivity issue
      if (error.code !== 'ECONNREFUSED') {
        showWarning(
          'Error al Cargar Proveedores',
          'No se pudieron cargar los proveedores. Algunas funciones pueden no estar disponibles.'
        );
      }
      
      // Set empty state
      setSuppliers([]);
    }
  };

  // Load purchases when tab becomes active or filters change
  useEffect(() => {
    if (activeTab === 'purchases') {
      fetchPurchases(purchasePage);
      fetchSuppliers();
      fetchPurchaseAvailableDates();
    }
  }, [activeTab, purchaseStatusFilter, purchaseDateFilter, purchaseSupplierFilter, purchasePage]);

  // Handle purchase filter changes
  const handlePurchaseStatusFilterChange = (status) => {
    setPurchaseStatusFilter(status);
    setPurchasePage(1);
  };

  const handlePurchaseDateFilterChange = (date) => {
    setPurchaseDateFilter(date);
    setPurchasePage(1);
  };

  const handlePurchaseSupplierFilterChange = (supplierId) => {
    setPurchaseSupplierFilter(supplierId);
    setPurchasePage(1);
  };

  const handlePurchasePageChange = (newPage) => {
    setPurchasePage(newPage);
  };

  // Modal handlers
  const openSupplierModal = (supplier = null) => {
    setSelectedSupplier(supplier);
    setShowSupplierModal(true);
  };

  const closeSupplierModal = () => {
    setShowSupplierModal(false);
    setSelectedSupplier(null);
  };

  const openPurchaseModal = async (purchase = null) => {
    setSelectedPurchase(purchase);
    setShowPurchaseModal(true);
  };

  const closePurchaseModal = () => {
    setShowPurchaseModal(false);
    setSelectedPurchase(null);
    setSearchResults([]); // Clear products when closing modal
  };

  // Save supplier
  const handleSaveSupplier = async (supplierData) => {
    try {
      setSupplierModalLoading(true);
      
      let response;
      if (selectedSupplier) {
        response = await purchaseApi.suppliers.update(selectedSupplier.id, supplierData);
      } else {
        response = await purchaseApi.suppliers.create(supplierData);
      }
      
      // The API response includes success, message, and data
      if (response && response.success) {
        await fetchSuppliers(); // Refresh suppliers list
        closeSupplierModal();
        
        // Show success notification
        showSuccess(
          selectedSupplier ? 'Proveedor Actualizado' : 'Proveedor Creado',
          `${supplierData.name} ${selectedSupplier ? 'se actualizó' : 'se creó'} correctamente`
        );
      } else {
        throw new Error(response?.message || 'Error saving supplier');
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      
      // Show error notification
      showError(
        'Error al Guardar Proveedor',
        error.response?.data?.message || error.message || 'No se pudo guardar el proveedor. Verifique los datos e intente nuevamente.'
      );
    } finally {
      setSupplierModalLoading(false);
    }
  };

  // Save purchase
  const handleSavePurchase = async (purchaseData) => {
    try {
      setPurchaseModalLoading(true);
      
      // Validate purchase data
      if (!purchaseData.supplierId) {
        throw new Error('Debe seleccionar un proveedor');
      }
      
      if (!purchaseData.items || purchaseData.items.length === 0) {
        throw new Error('Debe agregar al menos un producto');
      }

      // Transform frontend data to backend format
      const transformedData = {
        supplierId: purchaseData.supplierId,
        expectedDelivery: purchaseData.expectedDelivery,
        notes: purchaseData.notes || '',
        items: purchaseData.items.map(item => ({
          productId: item.productId,
          productName: item.productName,
          category: item.category,
          quantityOrdered: item.quantity,
          unitCost: Math.round(item.expectedPrice * 100) // Convert to centavos if needed or pesos
        }))
      };
      
      let response;
      if (selectedPurchase) {
        response = await purchaseApi.purchases.update(selectedPurchase.id, transformedData);
      } else {
        response = await purchaseApi.purchases.create(transformedData);
      }
      
      if (response.success) {
        await fetchPurchases(purchasePage); // Refresh purchases list
        closePurchaseModal();
        
        // Show success notification
        const supplier = suppliers.find(s => s.id === purchaseData.supplierId);
        showSuccess(
          selectedPurchase ? 'Orden Actualizada' : 'Orden Creada',
          `Orden de compra para ${supplier?.name || 'proveedor'} ${selectedPurchase ? 'actualizada' : 'creada'} correctamente`
        );
      } else {
        throw new Error(response.message || 'Error saving purchase');
      }
    } catch (error) {
      console.error('Error saving purchase:', error);
      
      // Show error notification
      showError(
        'Error en Orden de Compra',
        error.response?.data?.message || error.message || 'No se pudo guardar la orden de compra. Verifique los datos e intente nuevamente.'
      );
    } finally {
      setPurchaseModalLoading(false);
    }
  };

  // Helper functions for purchase status
  const getPurchaseStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12'; // orange
      case 'received': return '#27ae60'; // green
      case 'partial': return '#3498db'; // blue
      case 'cancelled': return '#e74c3c'; // red
      default: return '#95a5a6'; // gray
    }
  };

  const formatPurchaseDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  // Calculate payment date based on payment method
  const calculatePaymentDate = (purchase) => {
    const paymentMethodCode = purchase.paymentMethod?.code;
    const orderDate = new Date(purchase.orderDate);
    
    if (!paymentMethodCode || isNaN(orderDate.getTime())) return '-';
    
    switch (paymentMethodCode) {
      case 'cash': // Efectivo
      case 'debit': // Débito  
      case 'currentaccount': // Cuenta Corriente
      case 'banktransfer': // Transferencia Bancaria
      case 'otherelectronic': // Otro Medio Electrónico
        return formatPurchaseDate(purchase.orderDate);
        
      case 'check30': // Cheque 30 días
        const payDate30 = new Date(orderDate);
        payDate30.setDate(payDate30.getDate() + 30);
        return formatPurchaseDate(payDate30.toISOString());
        
      case 'check60': // Cheque 60 días
        const payDate60 = new Date(orderDate);
        payDate60.setDate(payDate60.getDate() + 60);
        return formatPurchaseDate(payDate60.toISOString());
        
      case 'check90': // Cheque 90 días
        const payDate90 = new Date(orderDate);
        payDate90.setDate(payDate90.getDate() + 90);
        return formatPurchaseDate(payDate90.toISOString());
        
      case 'cashondelivery': // Efectivo Contra Entrega
        // Payment date equals delivery date (when status becomes 'received')
        return purchase.status === 'received' && purchase.deliveryDate 
          ? formatPurchaseDate(purchase.deliveryDate)
          : 'Pendiente';
        
      default:
        return '-';
    }
  };

  // Format payment method names for display
  const formatPaymentMethodName = (paymentMethodName) => {
    if (!paymentMethodName) return 'N/A';
    
    // Replace "Efectivo contra Entrega" with "Contraentrega"
    if (paymentMethodName === 'Efectivo contra Entrega') {
      return 'Contraentrega';
    }
    
    return paymentMethodName;
  };

  // Status Update Functions
  const handleStatusBadgeClick = (purchase) => {
    setSelectedPurchaseForStatus(purchase);
    setSelectedStatus(purchase.status);
    setStatusNote('');
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedPurchaseForStatus || !selectedStatus) return;

    // Require note for PARCIAL and CANCELADO
    if ((selectedStatus === 'PARCIAL' || selectedStatus === 'CANCELADO') && !statusNote.trim()) {
      alert('Debe agregar una nota para estados Parcial o Cancelado');
      return;
    }

    try {
      setStatusModalLoading(true);

      // Map Spanish status to English for API
      const statusMapping = {
        'PENDIENTE': 'pending',
        'PARCIAL': 'partial',
        'COMPLETO': 'received',
        'CANCELADO': 'cancelled'
      };

      const apiStatus = statusMapping[selectedStatus];
      if (!apiStatus) {
        throw new Error('Invalid status selection');
      }

      // Prepare update data
      const updateData = {
        status: apiStatus,
        deliveryDate: selectedStatus === 'COMPLETO' ? new Date().toISOString() : null
      };

      // Add note for partial status
      if (selectedStatus === 'PARCIAL' && statusNote.trim()) {
        updateData.partialNote = statusNote.trim();
      }

      // If it's Contraentrega and status is COMPLETO, we'll update the payment date
      const isContraentrega = selectedPurchaseForStatus.paymentMethod?.name === 'Efectivo contra Entrega';
      
      // For CANCELADO status, use the cancel endpoint
      let response;
      if (selectedStatus === 'CANCELADO') {
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/purchases/${selectedPurchaseForStatus.id}/cancel`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reason: statusNote.trim() })
        });
      } else {
        // Update purchase status via API
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/purchases/${selectedPurchaseForStatus.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        });
      }

      if (!response.ok) {
        throw new Error('Failed to update purchase status');
      }

      // No need for separate note update since backend handles it directly

      // Refresh the purchases table
      await fetchPurchases(purchasePage);
      
      // Close modal
      setShowStatusModal(false);
      setSelectedPurchaseForStatus(null);
      setSelectedStatus('');
      setStatusNote('');

      console.log('Purchase status updated successfully');
      
    } catch (error) {
      console.error('Error updating purchase status:', error);
      alert('Error al actualizar el estado: ' + error.message);
    } finally {
      setStatusModalLoading(false);
    }
  };

  const updatePurchaseNote = async (purchaseId, note) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/purchases/${purchaseId}/note`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note })
      });

      if (!response.ok) {
        throw new Error('Failed to update purchase note');
      }
    } catch (error) {
      console.error('Error updating purchase note:', error);
      throw error;
    }
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedPurchaseForStatus(null);
    setSelectedStatus('');
    setStatusNote('');
  };

  // Purchase Download Functions
  const checkDateRangeAvailability = async () => {
    try {
      setAvailabilityLoading(true);
      const response = await fetch(`http://192.168.1.34:3000/api/purchases/availability/check`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setDateRangeAvailability(result.data);
      } else {
        console.error('Failed to check availability:', result.message);
        // Use default availability if check fails
        setDateRangeAvailability({
          all: { day: true, week: true, month: true, threemonths: true },
          bySupplier: {}
        });
      }
    } catch (error) {
      console.error('Error checking date range availability:', error);
      // Use default availability if check fails
      setDateRangeAvailability({
        all: { day: true, week: true, month: true, threemonths: true },
        bySupplier: {}
      });
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleDownloadHistorialClick = async () => {
    setShowDownloadModal(true);
    await checkDateRangeAvailability();
  };

  const closeDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadSupplier('');
    setDownloadDateRange('month');
  };

  // Handle supplier change in download modal
  const handleDownloadSupplierChange = (e) => {
    const newSupplier = e.target.value;
    setDownloadSupplier(newSupplier);
    
    // Check if current date range is available for new supplier
    const availabilityToCheck = newSupplier === '' ? 
      dateRangeAvailability.all : 
      dateRangeAvailability.bySupplier[newSupplier]?.periods || {};
    
    // If current selection is not available for new supplier, reset to first available option
    if (!availabilityToCheck[downloadDateRange]) {
      const availableRanges = ['month', 'threemonths', 'week', 'day'];
      const firstAvailable = availableRanges.find(range => availabilityToCheck[range]);
      if (firstAvailable) {
        setDownloadDateRange(firstAvailable);
      }
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      let downloadUrl = '';
      let filename = '';
      
      // Build URL based on date range
      switch (downloadDateRange) {
        case 'day':
          const today = new Date().toISOString().split('T')[0];
          downloadUrl = `${baseUrl}/api/purchases/export/day/${today}`;
          filename = `Compras_${today.replace(/-/g, '_')}.xlsx`;
          break;
        case 'week':
          downloadUrl = `${baseUrl}/api/purchases/export/week`;
          filename = `Compras_Semana_${new Date().toISOString().split('T')[0].replace(/-/g, '_')}.xlsx`;
          break;
        case 'month':
          downloadUrl = `${baseUrl}/api/purchases/export/month`;
          filename = `Compras_Mes_${new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(' ', '_')}.xlsx`;
          break;
        case 'threemonths':
          downloadUrl = `${baseUrl}/api/purchases/export/threemonths`;
          filename = `Compras_3_Meses_${new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(' ', '_')}.xlsx`;
          break;
        default:
          downloadUrl = `${baseUrl}/api/purchases/export/month`;
          filename = `Compras_Mes_${new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }).replace(' ', '_')}.xlsx`;
      }
      
      // Add supplier filter if selected
      if (downloadSupplier) {
        downloadUrl += `?supplierId=${downloadSupplier}`;
      }
      
      console.log('Download URL:', downloadUrl);
      console.log('Filename:', filename);
      
      // Create download request
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      // Check if response is actually an Excel file
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('spreadsheet') && !contentType.includes('excel')) {
        const responseText = await response.text();
        console.error('Unexpected response type:', responseText);
        throw new Error('La respuesta no es un archivo Excel válido');
      }
      
      // Create blob and download
      const blob = await response.blob();
      console.log('Blob size:', blob.size);
      
      if (blob.size === 0) {
        throw new Error('El archivo descargado está vacío');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Download completed successfully');
      
      // Close modal
      closeDownloadModal();
      
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      alert('Error al descargar el archivo: ' + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Sales Download Functions
  const checkSalesAvailability = async () => {
    try {
      setSalesAvailabilityLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/orders/availability/check`);
      const result = await response.json();
      
      if (result.success) {
        setSalesAvailability(result.availability);
        console.log('Sales availability:', result.availability);
      } else {
        console.error('Failed to check sales availability:', result.message);
        // Use default availability if check fails
        setSalesAvailability({
          day: true,
          week: true,
          month: true,
          threemonths: true
        });
      }
    } catch (error) {
      console.error('Error checking sales availability:', error);
      // Use default availability if check fails
      setSalesAvailability({
        day: true,
        week: true,
        month: true,
        threemonths: true
      });
    } finally {
      setSalesAvailabilityLoading(false);
    }
  };

  const handleSalesDownloadClick = async () => {
    setShowSalesDownloadModal(true);
    await checkSalesAvailability();
  };

  const closeSalesDownloadModal = () => {
    setShowSalesDownloadModal(false);
    setSalesDownloadDateRange('month');
  };

  const handleSalesDownloadExcel = async () => {
    try {
      setSalesDownloadLoading(true);
      console.log('📊 Starting sales download for range:', salesDownloadDateRange);
      
      let url;
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      
      switch (salesDownloadDateRange) {
        case 'day':
          const today = new Date().toISOString().split('T')[0];
          url = `${baseUrl}/api/orders/export/day/${today}`;
          break;
        case 'week':
          url = `${baseUrl}/api/orders/export/week`;
          break;
        case 'month':
          url = `${baseUrl}/api/orders/export/month`;
          break;
        case 'threemonths':
          url = `${baseUrl}/api/orders/export/threemonths`;
          break;
        default:
          throw new Error('Invalid date range selected');
      }
      
      console.log('📋 Making request to:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
      
      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', Object.fromEntries(response.headers.entries()));
      
      const contentType = response.headers.get('content-type');
      console.log('📊 Content type:', contentType);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No se encontraron pedidos para el período seleccionado');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
      }
      
      // Validate content type
      if (!contentType || !contentType.includes('spreadsheet')) {
        console.warn('⚠️ Unexpected content type:', contentType);
      }
      
      const blob = await response.blob();
      console.log('📊 Blob size:', blob.size, 'bytes');
      
      if (blob.size === 0) {
        throw new Error('El archivo descargado está vacío');
      }
      
      // Extract filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'historial_ventas.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          console.log('📊 Extracted filename:', filename);
        }
      }
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      console.log('✅ Sales download completed successfully');
      closeSalesDownloadModal();
      
    } catch (error) {
      console.error('❌ Error downloading sales file:', error);
      alert(`Error al descargar el archivo: ${error.message}`);
    } finally {
      setSalesDownloadLoading(false);
    }
  };

  // Analytics Handler (Phase 4.1)
  const handleAnalyticsClick = async (analyticsType) => {
    try {
      setAnalyticsLoading(true);
      setSelectedAnalyticsType(analyticsType);
      
      // Temporarily disable payment methods modal
      if (analyticsType === 'payment-methods') {
        showInfo(
          'Función en Desarrollo',
          'El análisis detallado de métodos de pago estará disponible próximamente'
        );
        setAnalyticsLoading(false);
        return;
      }
      
      // Show info notification for other analytics
      showInfo(
        'Cargando Análisis',
        `Preparando reporte de ${getAnalyticsTitle(analyticsType)}...`
      );
      
      // Simulate API call (replace with actual API when backend is ready)
      setTimeout(() => {
        setAnalyticsLoading(false);
        showSuccess(
          'Análisis Completado',
          `Reporte de ${getAnalyticsTitle(analyticsType)} listo para revisar`
        );
        // Here you would normally show a modal or navigate to detailed view
      }, 1500);
      
    } catch (error) {
      console.error('Error loading analytics:', error);
      setAnalyticsLoading(false);
      showError(
        'Error en Análisis',
        'No se pudo cargar el reporte. Intente nuevamente.'
      );
    }
  };

  const getAnalyticsTitle = (type) => {
    const titles = {
      'cogs': 'Análisis de Costos (COGS)',
      'supplier-performance': 'Rendimiento de Proveedores',
      'daily-profit': 'Ganancia Diaria',
      'inventory-turnover': 'Rotación de Inventario',
      'business-health': 'Salud del Negocio',
      'payment-methods': 'Métodos de Pago'
    };
    return titles[type] || 'Análisis';
  };

  // Fetch detailed payment methods data for modal
  const fetchPaymentMethodsDetails = async () => {
    try {
      setPaymentMethodsModalLoading(true);
      const filters = getDateFilters(paymentMethodsPeriod);
      const stats = await apiService.getDashboardStats(filters);
      
      // Transform and enhance the payment methods data
      const paymentMethodsData = stats.paymentMethods || [];
      
      const paymentLabels = {
        'efectivo': 'Efectivo',
        'debito': 'Débito', 
        'credito': 'Crédito',
        'qr': 'QR',
        'transferencia': 'Transferencia',
        'rappi': 'Rappi',
        'pedidosya': 'PedidosYa'
      };
      
      const paymentColors = {
        'efectivo': '#27ae60',
        'debito': '#3498db',
        'credito': '#9b59b6',
        'qr': '#e67e22',
        'transferencia': '#1abc9c',
        'rappi': '#ff441f',
        'pedidosya': '#f52f41'
      };
      
      const totalRevenue = paymentMethodsData.reduce((sum, pm) => sum + pm.total, 0);
      const totalTransactions = paymentMethodsData.reduce((sum, pm) => sum + pm.count, 0);
      
      const enhancedData = paymentMethodsData.map(pm => ({
        method: paymentLabels[pm.method] || pm.method,
        originalMethod: pm.method,
        value: pm.total, // Backend already returns values in pesos
        count: pm.count,
        color: paymentColors[pm.method] || '#95a5a6',
        percentage: totalRevenue > 0 ? ((pm.total / totalRevenue) * 100) : 0,
        avgTransaction: pm.count > 0 ? pm.total / pm.count : 0
      })).sort((a, b) => b.value - a.value); // Sort by value descending
      
      setPaymentMethodsModalData({
        methods: enhancedData,
        totalRevenue: totalRevenue, // Backend already returns values in pesos
        totalTransactions,
        period: paymentMethodsPeriod
      });
      
    } catch (error) {
      console.error('Error fetching payment methods details:', error);
      showError(
        'Error al Cargar Datos',
        'No se pudieron cargar los detalles de métodos de pago'
      );
    } finally {
      setPaymentMethodsModalLoading(false);
    }
  };

  // Get stats for current period
  const getTodayStats = () => {
    // "Ventas hoy" — from /analytics/v2/daily-sales-progress (same as HorizontePyME VENTAS HOY)
    const revenue = dailySalesData?.current?.sales
      ?? dashboardStats?.summary?.totalRevenue
      ?? 0;
    const orders = dailySalesData?.current?.invoiceCount
      ?? dashboardStats?.summary?.totalOrders
      ?? 0;

    // "Ticket Promedio" — from /analytics/average-invoice-value (same as HorizontePyME Promedio por Factura)
    const averageOrder = averageInvoiceData?.AverageInvoiceValue
      ?? dashboardStats?.summary?.averageOrderValue
      ?? 0;

    // "Más Vendido Hoy" — from /analytics/best-sellers?period=day (same as HorizontePyME BESTSELLERS HOY)
    const bestSeller = bestSellersData.length > 0
      ? bestSellersData[0].ProductName
      : (todayTopProductsData && todayTopProductsData.length > 0
          ? todayTopProductsData[0].productName
          : '-');
    const bestSellerCount = bestSellersData.length > 0
      ? parseFloat(bestSellersData[0].TotalQuantitySold || 0)
      : (todayTopProductsData && todayTopProductsData.length > 0
          ? todayTopProductsData[0].quantitySold
          : 0);

    return { revenue, orders, averageOrder, bestSeller, bestSellerCount };
  };

  const todayStats = getTodayStats();

  // Transform backend data to chart format
  const getSalesByCategory = () => {
    try {
      if (!Array.isArray(salesByCategoryData) || salesByCategoryData.length === 0) return [];
      return salesByCategoryData.map(cat => ({
        name: cat.category || cat.categoryName || '',
        value: parseFloat(cat.totalRevenue || cat.revenue || 0),
        color: getCategoryColor(cat.category || cat.categoryName || ''),
      }));
    } catch (e) {
      console.error('getSalesByCategory error:', e);
      return [];
    }
  };

  const getTopProducts = () => {
    // "Top 5 Ventas" — use v2 data from /analytics/top-products (matches HorizontePyME TopProductsToday)
    const source = topProductsV2Data.length > 0 ? topProductsV2Data : (topProductsData || []);
    if (source.length === 0) return [];

    return source.slice(0, 5).map(product => ({
      // v2 fields: ProductName, TotalQuantity, TotalRevenue
      // legacy fields: productName, quantitySold, revenue
      name: product.ProductName || product.productName || '',
      sold: parseFloat(product.TotalQuantity || product.quantitySold || 0),
      revenue: parseFloat(product.TotalRevenue || product.revenue || 0),
    }));
  };

  const getPaymentMethods = () => {
    if (!paymentMethodsData || paymentMethodsData.length === 0) return [];
    
    const paymentColors = {
      'efectivo': '#27ae60',
      'debito': '#3498db',
      'credito': '#9b59b6',
      'qr': '#e67e22',
      'transferencia': '#1abc9c',
      'rappi': '#ff441f',
      'pedidosya': '#f52f41'
    };
    
    const paymentLabels = {
      'efectivo': 'Efectivo',
      'debito': 'Débito',
      'credito': 'Crédito',
      'qr': 'QR',
      'transferencia': 'Transferencia',
      'rappi': 'Rappi',
      'pedidosya': 'Pedidos Ya'
    };
    
    const totalRevenue = paymentMethodsData.reduce((sum, pm) => sum + pm.total, 0);
    
    return paymentMethodsData.map(pm => ({
      method: paymentLabels[pm.method] || pm.method,
      value: pm.total, // Backend already returns values in pesos
      count: pm.count,
      color: paymentColors[pm.method] || '#95a5a6',
      percentage: totalRevenue > 0 ? ((pm.total / totalRevenue) * 100) : 0
    }));
  };

  const getFinancialData = () => {
    if (!dashboardStats) {
      return {
        totalIncome: 0,
        totalExpenses: 0,
        netProfit: 0,
        profitMargin: 0,
        cashOnHand: 0,
        pendingCollections: 0,
        averageTransactionValue: 0,
        dailyGoal: 120000,
        goalProgress: 0
      };
    }
    
    const totalIncome = dashboardStats.summary?.totalRevenue || 0;
    const totalOrders = dashboardStats.summary?.totalOrders || 0;
    const avgOrder = totalOrders > 0 ? totalIncome / totalOrders : 0;
    
    // Calculate cash on hand from payment methods
    const cashMethod = dashboardStats.paymentMethods?.find(pm => pm.paymentMethod === 'efectivo');
    const cashOnHand = cashMethod?.totalRevenue || 0;
    
    // Mock expenses (30% of revenue for realistic simulation)
    const totalExpenses = Math.round(totalIncome * 0.30);
    const netProfit = totalIncome - totalExpenses;
    const profitMargin = totalIncome > 0 ? ((netProfit / totalIncome) * 100) : 0;
    
    // Daily goal (mock - can be configured)
    const dailyGoal = 120000;
    const goalProgress = totalIncome > 0 ? ((totalIncome / dailyGoal) * 100) : 0;
    
    return {
      totalIncome,
      totalExpenses,
      netProfit,
      profitMargin,
      cashOnHand,
      pendingCollections: 0, // Not tracked yet
      averageTransactionValue: Math.round(avgOrder),
      dailyGoal,
      goalProgress
    };
  };

  // Only compute data if dashboardStats is loaded
  const salesByCategory = getSalesByCategory();
  const topProducts = getTopProducts();
  const paymentMethods = getPaymentMethods();
  const finances = dashboardStats ? getFinancialData() : {
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
    profitMargin: 0,
    cashOnHand: 0,
    pendingCollections: 0,
    averageTransactionValue: 0,
    dailyGoal: 120000,
    goalProgress: 0
  };

  const financialData = {
    today: finances,
    week: finances,
    month: finances
  };

  const mockProducts = [
    { id: 1, name: 'FRANCES', price: 3100, category: 'Pan', stock: 150 },
    { id: 2, name: 'DOCENA MEDIALUNAS', price: 20500, category: 'Facturas', stock: 45 },
    { id: 3, name: 'ALFAJORES DE MAICENA', price: 3300, category: 'Dulces', stock: 89 }
  ];

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0';
    // Amount is already in pesos, just round it
    return `$${Math.round(amount)}`;
  };

  const renderOverview = () => (
    <div className="overview-content">
      <div className="stats-grid">
        <div className="stat-card revenue">
          <div className="stat-icon">
            <DollarSign size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Ventas de Hoy</div>
            <div className="stat-value">{formatCurrency(todayStats.revenue)}</div>
          </div>
        </div>

        <div className="stat-card orders">
          <div className="stat-icon">
            <ShoppingCart size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Tickets Hoy</div>
            <div className="stat-value">{todayStats.orders || 0}</div>
          </div>
        </div>

        <div className="stat-card average">
          <div className="stat-icon">
            <TrendingUp size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Ticket Promedio</div>
            <div className="stat-value">{formatCurrency(todayStats.averageOrder)}</div>
          </div>
        </div>

        <div className="stat-card bestseller">
          <div className="stat-icon">
            <SquareStar size={32} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Más Vendido Hoy</div>
            <div className="stat-value">{todayStats.bestSeller || '-'}</div>
            <div className="stat-change">{todayStats.bestSellerCount || 0} unidades</div>
          </div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-header">
            <h3>Ventas por Categoría</h3>
            <div className="period-tabs">
              <button 
                className={`period-tab ${categoryPeriod === 'today' ? 'active' : ''}`}
                onClick={() => setCategoryPeriod('today')}
              >
                Hoy
              </button>
              <button 
                className={`period-tab ${categoryPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setCategoryPeriod('week')}
              >
                Semana
              </button>
              <button 
                className={`period-tab ${categoryPeriod === 'month' ? 'active' : ''}`}
                onClick={() => setCategoryPeriod('month')}
              >
                Mes
              </button>
            </div>
          </div>
          <div className="chart-content">
            <div className="category-bars">
              {salesByCategory.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No hay datos de ventas por categoría
                </div>
              ) : (
                salesByCategory.map(cat => {
                  const maxValue = Math.max(...salesByCategory.map(c => c.value));
                  return (
                    <div key={cat.name} className="category-bar-item">
                      <div className="category-bar-label">{cat.name}</div>
                      <div className="category-bar-container">
                        <div 
                          className="category-bar-fill" 
                          style={{ 
                            width: `${(cat.value / maxValue) * 100}%`,
                            backgroundColor: cat.color 
                          }}
                        >
                          <span className="category-bar-value">{formatCurrency(cat.value)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-header">
            <h3>Top 5 Ventas</h3>
            <div className="period-tabs">
              <button 
                className={`period-tab ${productsPeriod === 'today' ? 'active' : ''}`}
                onClick={() => setProductsPeriod('today')}
              >
                Hoy
              </button>
              <button 
                className={`period-tab ${productsPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setProductsPeriod('week')}
              >
                Semana
              </button>
              <button 
                className={`period-tab ${productsPeriod === 'month' ? 'active' : ''}`}
                onClick={() => setProductsPeriod('month')}
              >
                Mes
              </button>
            </div>
          </div>
          <div className="chart-content">
            <div className="top-products-list">
              {topProducts.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  No hay datos de productos
                </div>
              ) : (
                topProducts.map((product, index) => (
                  <div key={product.name} className="top-product-item">
                    <div className="product-rank">#{index + 1}</div>
                    <div className="product-info">
                      <div className="product-name">
                        {product.name} <span className="product-stats">• {product.sold} vendidos • {formatCurrency(product.revenue)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFinances = () => (
    <div className="reportes-content">
      {/* Original Financial Layout */}
      <div className="finances-layout">
        {/* Left Column - Financial KPIs */}
        <div className="finance-stats-column">
          <div className="finance-stat-card income">
            <div className="finance-stat-icon">
              <ArrowUpRight size={28} />
            </div>
            <div className="finance-stat-content">
              <div className="finance-stat-label">Ingresos</div>
              <div className="finance-stat-value">{formatCurrency(finances.totalIncome)}</div>
              <div className="finance-stat-change positive">↑ {finances.goalProgress.toFixed(1)}% del objetivo</div>
            </div>
          </div>

          <div className="finance-stat-card expenses">
            <div className="finance-stat-icon">
              <ArrowDownRight size={28} />
            </div>
            <div className="finance-stat-content">
              <div className="finance-stat-label">Egresos</div>
              <div className="finance-stat-value">{formatCurrency(finances.totalExpenses)}</div>
              <div className="finance-stat-change">{((finances.totalExpenses / finances.totalIncome) * 100).toFixed(1)}% de ingresos</div>
            </div>
          </div>

          <div className="finance-stat-card ticket">
            <div className="finance-stat-icon">
              <TrendingUp size={28} />
            </div>
            <div className="finance-stat-content">
              <div className="finance-stat-label">Ticket Promedio</div>
              <div className="finance-stat-value">{formatCurrency(finances.averageTransactionValue)}</div>
              <div className="finance-stat-change positive">↑ 5.2% vs período anterior</div>
            </div>
          </div>
        </div>

        {/* Right Column - Payment Methods Chart */}
        <div className="chart-card payment-methods-card">
          <div className="chart-header">
            <div className="chart-title-section">
              <h3>Métodos de Pago</h3>
              <span className="chart-subtitle">Distribución de pagos por método</span>
            </div>
            <div className="period-tabs">
              <button 
                className={`period-tab ${financesPeriod === 'today' ? 'active' : ''}`}
                onClick={() => setFinancesPeriod('today')}
              >
                Hoy
              </button>
              <button 
                className={`period-tab ${financesPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setFinancesPeriod('week')}
              >
                Semana
              </button>
              <button 
                className={`period-tab ${financesPeriod === 'month' ? 'active' : ''}`}
                onClick={() => setFinancesPeriod('month')}
              >
                Mes
              </button>
            </div>
          </div>
          <div className="chart-content payment-methods-content">
          <div className="payment-methods-grid">
            {/* Donut Chart Visualization */}
            <div className="donut-chart-container">
              <svg viewBox="0 0 200 200" className="donut-chart">
                {/* Background circle */}
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke="#ecf0f1"
                  strokeWidth="40"
                />
                {/* Colored segments */}
                {(() => {
                  if (paymentMethods.length === 0) return null;
                  
                  const total = paymentMethods.reduce((sum, pm) => sum + pm.value, 0);
                  if (total === 0) return null;
                  
                  const circumference = 2 * Math.PI * 80;
                  let accumulatedOffset = 0;
                  
                  return paymentMethods.map((method, index) => {
                    const percentage = (method.value / total) * 100;
                    const segmentLength = (percentage / 100) * circumference;
                    const currentOffset = accumulatedOffset;
                    
                    accumulatedOffset += segmentLength;
                    
                    return (
                      <circle
                        key={method.method}
                        cx="100"
                        cy="100"
                        r="80"
                        fill="none"
                        stroke={method.color}
                        strokeWidth="40"
                        strokeDasharray={`${segmentLength} ${circumference}`}
                        strokeDashoffset={-currentOffset}
                        className="donut-segment"
                        transform="rotate(-90 100 100)"
                        style={{
                          transition: 'all 0.8s ease'
                        }}
                      />
                    );
                  });
                })()}
                {/* Center text */}
                <text
                  x="100"
                  y="95"
                  textAnchor="middle"
                  fontSize="24"
                  fontWeight="bold"
                  fill="#2c3e50"
                >
                  {paymentMethods.reduce((sum, pm) => sum + pm.count, 0)}
                </text>
                <text
                  x="100"
                  y="115"
                  textAnchor="middle"
                  fontSize="14"
                  fill="#7f8c8d"
                >
                  Pedidos
                </text>
              </svg>
            </div>

            {/* Payment Methods Legend & Stats */}
            <div className="payment-methods-list">
              {paymentMethods.map((method, index) => (
                <div key={method.method} className="payment-method-item">
                  <div className="payment-method-header">
                    <div className="payment-method-name">
                      <span 
                        className="payment-color-dot" 
                        style={{ backgroundColor: method.color }}
                      />
                      <span className="method-label">{method.method}</span>
                    </div>
                    <div className="payment-method-percentage">
                      {method.percentage.toFixed(1)}%
                    </div>
                  </div>
                  <div className="payment-method-stats">
                    <span className="payment-count">{method.count} pedidos</span>
                    <span className="payment-amount">{formatCurrency(method.value)}</span>
                  </div>
                  <div className="payment-progress-bar">
                    <div 
                      className="payment-progress-fill"
                      style={{ 
                        width: `${method.percentage}%`,
                        backgroundColor: method.color
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );

  const renderReports = () => (
    <div className="reportes-content">
      {/* Analytics Action Cards - Phase 4.1 */}
      <div className="reportes-actions-grid">
        {/* COGS Analysis */}
        <div className="report-action-card cogs" onClick={() => handleAnalyticsClick('cogs')}>
          <div className="report-action-icon">
            <Calculator size={24} />
          </div>
          <div className="report-action-content">
            <div className="report-action-title">Análisis de Costos</div>
            <div className="report-action-description">Costo de los Bienes Vendidos (COGS) y márgenes</div>
            <div className="report-action-metric positive">Margen: {analyticsMetrics.averageMargin.toFixed(1)}% ↑</div>
          </div>
        </div>

        {/* Supplier Performance */}
        <div className="report-action-card supplier-performance" onClick={() => handleAnalyticsClick('supplier-performance')}>
          <div className="report-action-icon">
            <Users size={24} />
          </div>
          <div className="report-action-content">
            <div className="report-action-title">Rendimiento Proveedores</div>
            <div className="report-action-description">Evaluación y ranking de proveedores</div>
            <div className="report-action-metric neutral">{analyticsMetrics.suppliersCount} Proveedores</div>
          </div>
        </div>

        {/* Daily Profit Analysis */}
        <div className="report-action-card daily-profit" onClick={() => handleAnalyticsClick('daily-profit')}>
          <div className="report-action-icon">
            <TrendingUp size={24} />
          </div>
          <div className="report-action-content">
            <div className="report-action-title">Ganancia Diaria</div>
            <div className="report-action-description">Análisis de ingresos vs costos diarios</div>
            <div className="report-action-metric positive">Hoy: {formatCurrency(analyticsMetrics.dailyProfit)}</div>
          </div>
        </div>

        {/* Inventory Turnover */}
        <div className="report-action-card inventory-turnover" onClick={() => handleAnalyticsClick('inventory-turnover')}>
          <div className="report-action-icon">
            <Package size={24} />
          </div>
          <div className="report-action-content">
            <div className="report-action-title">Rotación Inventario</div>
            <div className="report-action-description">Análisis de desperdicio y stock</div>
            <div className="report-action-metric negative">Desperdicio: {analyticsMetrics.wastePercentage.toFixed(1)}%</div>
          </div>
        </div>

        {/* Business Health Score */}
        <div className="report-action-card business-health" onClick={() => handleAnalyticsClick('business-health')}>
          <div className="report-action-icon">
            <PieChart size={24} />
          </div>
          <div className="report-action-content">
            <div className="report-action-title">Salud del Negocio</div>
            <div className="report-action-description">Índice general de rendimiento</div>
            <div className="report-action-metric positive">Score: {analyticsMetrics.businessHealthScore}/100</div>
          </div>
        </div>

        {/* Payment Methods Analysis */}
        <div className="report-action-card payment-methods" onClick={() => handleAnalyticsClick('payment-methods')}>
          <div className="report-action-icon">
            <CreditCard size={24} />
          </div>
          <div className="report-action-content">
            <div className="report-action-title">Métodos de Pago</div>
            <div className="report-action-description">Distribución y rentabilidad por método</div>
            <div className="report-action-metric neutral">{analyticsMetrics.paymentMethodsCount} Métodos</div>
          </div>
        </div>
      </div>

      {/* Analytics Insights Section */}
      <div className="analytics-insights-section">
        <div className="analytics-insights-header">
          <Zap size={20} />
          <h3>Insights Automáticos</h3>
        </div>
        <div className="analytics-insights-grid">
          {/* Top Margin Product */}
          <div className="insight-card success">
            <div className="insight-header">
              <div className="insight-icon">
                <Target size={16} />
              </div>
              <h4 className="insight-title">Producto Más Rentable</h4>
            </div>
            <div className="insight-description">
              Café Americano tiene el mayor margen de ganancia
            </div>
            <div className="insight-value success">Margen: 67%</div>
          </div>

          {/* Cost Alert */}
          <div className="insight-card warning">
            <div className="insight-header">
              <div className="insight-icon">
                <AlertTriangle size={16} />
              </div>
              <h4 className="insight-title">Alerta de Costos</h4>
            </div>
            <div className="insight-description">
              {analyticsMetrics?.costAlert?.alertMessage || 'Precio de harina aumentó 15% este mes'}
            </div>
            <div className="insight-value warning">
              {analyticsMetrics?.costAlert 
                ? `${analyticsMetrics.costAlert.supplier}: +${analyticsMetrics.costAlert.increasePercentage}%`
                : 'Los Andes: +$450'
              }
            </div>
          </div>

          {/* Best Supplier */}
          <div className="insight-card info">
            <div className="insight-header">
              <div className="insight-icon">
                <Users size={16} />
              </div>
              <h4 className="insight-title">Mejor Proveedor</h4>
            </div>
            <div className="insight-description">
              Distribuidora Los Andes: 98% entregas a tiempo
            </div>
            <div className="insight-value info">$37,150 este mes</div>
          </div>

          {/* Waste Alert */}
          <div className="insight-card danger">
            <div className="insight-header">
              <div className="insight-icon">
                <TrendingDownIcon size={16} />
              </div>
              <h4 className="insight-title">Reducir Desperdicio</h4>
            </div>
            <div className="insight-description">
              {analyticsMetrics?.wastePercentage 
                ? `Desperdicio actual: ${analyticsMetrics.wastePercentage}% del total de pedidos`
                : 'Productos lácteos: 8% desperdicio vs 3% promedio'
              }
            </div>
            <div className="insight-value danger">
              {analyticsMetrics?.wasteOrdersCount 
                ? analyticsMetrics.wasteOrdersCount > 0 
                  ? `-$${Math.round(77900 / 5 * 2).toLocaleString('es-AR')} esta semana`
                  : 'Sin desperdicios esta semana'
                : '-$77.900 total histórico'
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProducts = () => (
    <div className="products-content">
      {/* Product Actions Stats Grid */}
      <div className="products-actions-grid">
        {/* Actualizar Lista */}
        <label className="product-action-card import" style={{ cursor: isImporting ? 'not-allowed' : 'pointer', opacity: isImporting ? 0.6 : 1 }}>
          <div className="action-icon">
            {isImporting ? (
              <Loader size={24} className="rotating" />
            ) : (
              <Upload size={24} />
            )}
          </div>
          <div className="action-content">
            <div className="action-label">Actualizar Lista</div>
            <div className="action-description">{isImporting ? 'Importando...' : 'Actualizar Lista desde Excel'}</div>
          </div>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            disabled={isImporting}
            style={{ display: 'none' }}
          />
        </label>

        {/* Agregar Producto */}
        <div className="product-action-card add" onClick={handleAddProduct}>
          <div className="action-icon">
            <Plus size={24} />
          </div>
          <div className="action-content">
            <div className="action-label">Agregar Producto</div>
            <div className="action-description">Crear Nuevo Producto</div>
          </div>
        </div>

        {/* Exportar Excel */}
        <div className="product-action-card export" onClick={handleExportExcel}>
          <div className="action-icon">
            <ArrowRight size={24} />
          </div>
          <div className="action-content">
            <div className="action-label">Exportar Excel</div>
            <div className="action-description">Descargar Listado Completo</div>
          </div>
        </div>
      </div>

      {/* Add Product Form Modal */}
      {isAddingProduct && (
        <div className="add-product-overlay" onClick={handleCancelAdd}>
          <div className="add-product-form" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h4>Agregar Nuevo Producto</h4>
              <button 
                className="close-form-btn"
                onClick={handleCancelAdd}
                type="button"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="form-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre del Producto *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => handleNewProductChange('name', e.target.value)}
                    placeholder="Nombre del producto"
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Precio *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProduct.price}
                    onChange={(e) => handleNewProductChange('price', e.target.value)}
                    placeholder="0.00"
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Categoría</label>
                  <select
                    value={newProduct.category}
                    onChange={(e) => handleNewProductChange('category', e.target.value)}
                    className="form-select"
                  >
                    {categories.length > 0 ? (
                    categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="Pan">Pan</option>
                      <option value="Facturas">Facturas</option>
                      <option value="Dulces">Dulces</option>
                      <option value="Salados">Salados</option>
                      <option value="Aptos">Aptos</option>
                      <option value="Heladera">Heladera</option>
                      <option value="Congelados">Congelados</option>
                      <option value="Infusiones">Infusiones</option>
                      <option value="Regalos">Regalos</option>
                      <option value="Almacen">Almacen</option>
                    </>
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label>Stock Inicial</label>
                <input
                  type="number"
                  min="0"
                  value={newProduct.stock}
                  onChange={(e) => handleNewProductChange('stock', e.target.value)}
                  placeholder="10"
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Descripción</label>
              <textarea
                value={newProduct.description}
                onChange={(e) => handleNewProductChange('description', e.target.value)}
                placeholder="Descripción del producto (opcional)"
                className="form-textarea"
                rows="3"
              />
            </div>
            
            <div className="form-actions">
              <button 
                type="button"
                className="btn-secondary"
                onClick={handleCancelAdd}
              >
                Cancelar
              </button>
              <button 
                type="button"
                className="btn-primary"
                onClick={handleCreateProduct}
                disabled={isCreating || !newProduct.name || !newProduct.price}
              >
                {isCreating ? (
                  <>
                    <Loader size={16} className="spinning" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Crear Producto
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Import Results Display */}
      {showImportResults && importResults && (
        <div className="import-results-overlay">
          <div className="import-results-modal">
            <div className="import-results-header">
              <h4>Resultados de Importación</h4>
              <button 
                className="close-results-btn"
                onClick={() => setShowImportResults(false)}
                type="button"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="import-results-content">
              <div className="import-summary">
                <div className="summary-stats">
                  <div className="stat-item success">
                    <Check size={20} />
                    <span>{importResults.successful} Exitosos</span>
                  </div>
                  <div className="stat-item error">
                    <X size={20} />
                    <span>{importResults.failed} Fallos</span>
                  </div>
                  <div className="stat-item info">
                    <Package size={20} />
                    <span>{importResults.totalRows} Total</span>
                  </div>
                </div>
              </div>

              {importResults.updated && importResults.updated.length > 0 && (
                <div className="results-section">
                  <h5 className="section-title">✅ Productos Actualizados</h5>
                  <div className="results-list">
                    {importResults.updated.map((item, index) => (
                      <div key={index} className="result-item success">
                        <span className="row-number">Fila {item.row}</span>
                        <span className="product-name">{item.product}</span>
                        <span className="changes">
                          {item.changes.length > 0 ? item.changes.join(' / ') : 'Sin cambios específicos'} 
                          {item.userName && ` / ${item.userName}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResults.errors && importResults.errors.length > 0 && (
                <div className="results-section">
                  <h5 className="section-title">❌ Errores</h5>
                  <div className="results-list">
                    {importResults.errors.map((item, index) => (
                      <div key={index} className="result-item error">
                        <span className="row-number">Fila {item.row}</span>
                        <span className="product-name">{item.product}</span>
                        <span className="error-message">{item.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResults.skipped && importResults.skipped.length > 0 && (
                <div className="results-section">
                  <h5 className="section-title">⚠️ Omitidos</h5>
                  <div className="results-list">
                    {importResults.skipped.map((item, index) => (
                      <div key={index} className="result-item warning">
                        <span className="row-number">Fila {item.row}</span>
                        <span className="reason">{item.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="import-results-footer">
              <button 
                className="btn-primary"
                onClick={() => setShowImportResults(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Products Management Section */}
      <div className="products-unified-container">
        <div className="products-search-header">
          <h3>Gestión de Productos</h3>
          <div className="product-search-container">
            <div className="search-input-wrapper">
              <Search size={18} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar producto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-compact"
              />
              {isSearching && <div className="search-loading">...</div>}
            </div>
            
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                {searchResults.slice(0, 8).map((product) => (
                  <div
                    key={product.id}
                    className="search-result-item"
                    onClick={() => handleProductSelect(product)}
                  >
                    <span className="product-name">{product.name}</span>
                    <span className="product-price">{formatCurrency(product.price)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="products-table-section">
        {selectedProduct ? (
          <div className="single-product-table">
            <div className="table-header-compact">
              <span>Producto Seleccionado</span>
              <div className="table-header-actions">
                <button 
                  className="refresh-product-btn"
                  onClick={() => handleManualRefresh()}
                  title="Actualizar datos"
                  disabled={isRefreshing}
                >
                  <RefreshCw size={16} className={isRefreshing ? 'spin' : ''} />
                </button>
                <button 
                  className="clear-product-btn"
                  onClick={clearSelectedProduct}
                  title="Limpiar selección"
                >
                  ×
                </button>
              </div>
            </div>
            <table className="products-table-compact">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr key={selectedProduct.id}>
                  <td>{selectedProduct.id}</td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingProduct?.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      selectedProduct.name
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        value={editingProduct?.category || ''}
                        onChange={(e) => handleFieldChange('category', e.target.value)}
                        className="edit-select"
                      >
                        <option value="Panes">Panes</option>
                        <option value="Pastelería">Pastelería</option>
                        <option value="Galletas">Galletas</option>
                        <option value="Bebidas">Bebidas</option>
                        <option value="Dulces">Dulces</option>
                        <option value="Salado">Salado</option>
                        <option value="Sin Categoría">Sin Categoría</option>
                      </select>
                    ) : (
                      <span className="category-badge">{selectedProduct.category}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editingProduct?.price || ''}
                        onChange={(e) => handleFieldChange('price', e.target.value)}
                        className="edit-input"
                        step="1"
                        min="0"
                      />
                    ) : (
                      formatCurrency(selectedProduct.price)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editingProduct?.stock?.quantity || editingProduct?.stock || ''}
                        onChange={(e) => handleFieldChange('stock', { quantity: parseInt(e.target.value) || 0 })}
                        className="edit-input"
                        min="0"
                      />
                    ) : (
                      <span className={`stock-badge ${selectedProduct.stockStatus || 'unknown'}`}>
                        {selectedProduct.stock !== undefined ? selectedProduct.stock : 'N/A'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${selectedProduct.active ? 'active' : 'inactive'}`}>
                      {selectedProduct.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {isEditing ? (
                        <>
                          <button 
                            className="btn-icon success" 
                            title="Guardar cambios"
                            onClick={handleSaveEdit}
                            disabled={isSaving}
                          >
                            {isSaving ? <Loader size={16} className="spin" /> : <Check size={16} />}
                          </button>
                          <button 
                            className="btn-icon" 
                            title="Cancelar"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="btn-icon" 
                            title="Editar producto"
                            onClick={() => handleEditProduct(selectedProduct)}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            className="btn-icon danger" 
                            title="Eliminar producto"
                            onClick={() => handleDeleteProduct(selectedProduct.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? <Loader size={16} className="spin" /> : <Trash2 size={16} />}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            
            {/* Refresh indicator overlay */}
            {isRefreshing && (
              <div className="refresh-overlay">
                <div className="refresh-indicator">
                  <Loader size={20} className="spin" />
                  <span>Actualizando datos...</span>
                </div>
              </div>
            )}
            
            {/* Success indicator */}
            {refreshSuccess && (
              <div className="refresh-overlay success">
                <div className="refresh-indicator success">
                  <Check size={20} />
                  <span>¡Datos actualizados!</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="empty-table-message-compact">
            <div className="empty-state-compact">
              <Search size={40} className="empty-icon" />
              <h4>Selecciona un producto</h4>
              <p>Usa el buscador para encontrar y seleccionar un producto.</p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );

  const renderPurchases = () => (
    <div className="purchases-content">
      {/* Purchase Actions Stats Grid */}
      <div className="purchases-actions-grid">
        {/* Nueva Compra */}
        <div 
          className="purchase-action-card new-purchase clickable"
          onClick={() => openPurchaseModal()}
        >
          <div className="action-icon">
            <ShoppingCart size={24} />
          </div>
          <div className="action-content">
            <div className="action-label">Nueva Compra</div>
            <div className="action-description">Crear orden de compra</div>
          </div>
        </div>

        {/* Nuevo Proveedor */}
        <div 
          className="purchase-action-card new-supplier clickable"
          onClick={() => openSupplierModal()}
        >
          <div className="action-icon">
            <Handshake size={24} />
          </div>
          <div className="action-content">
            <div className="action-label">Nuevo Proveedor</div>
            <div className="action-description">Agregar proveedor</div>
          </div>
        </div>

        {/* Exportar Registros */}
        <div className="purchase-action-card export-records clickable" onClick={handleDownloadHistorialClick}>
          <div className="action-icon">
            <Download size={24} />
          </div>
          <div className="action-content">
            <div className="action-label">Exportar Registros</div>
            <div className="action-description">Descargar historial</div>
          </div>
        </div>
      </div>

      {/* Unified Purchases Management Section */}
      <div className="purchases-unified-container">
        <div className="purchases-search-header">
          <h3>Historial de Compras</h3>
          <div className="purchases-filters">
            <select 
              className="filter-select"
              value={purchaseStatusFilter}
              onChange={(e) => handlePurchaseStatusFilterChange(e.target.value)}
            >
              <option value="">Todos Los Estados</option>
              <option value="pending">Pendiente</option>
              <option value="received">Recibido</option>
              <option value="partial">Parcial</option>
              <option value="cancelled">Cancelado</option>
            </select>
            <select 
              className="filter-select supplier-filter"
              value={purchaseSupplierFilter}
              onChange={(e) => handlePurchaseSupplierFilterChange(e.target.value)}
            >
              <option value="">Todos Los Proveedores</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
            <OrderDatePicker
              selectedDate={purchaseDateFilter}
              onDateChange={handlePurchaseDateFilterChange}
              availableDates={purchaseAvailableDates}
            />
            {purchasesLoading && (
              <div className="loading-indicator">
                <Loader size={16} className="spinning" />
              </div>
            )}
          </div>
        </div>

        {/* Combined Filters and Pagination Info */}
        <div className="purchase-filters-pagination-combined">
          <div className="purchase-filters-and-results">
            {(purchaseDateFilter || purchaseStatusFilter || purchaseSupplierFilter) && (
              <div className="purchase-active-filters">
                <span className="purchase-filters-label">Filtros activos:</span>
                {purchaseDateFilter && (
                  <span className="purchase-filter-tag">
                    {purchaseDateFilter}
                    <button onClick={() => handlePurchaseDateFilterChange('')} className="purchase-filter-remove">×</button>
                  </span>
                )}
                {purchaseStatusFilter && (
                  <span className="purchase-filter-tag">
                    {purchaseStatusFilter}
                    <button onClick={() => handlePurchaseStatusFilterChange('')} className="purchase-filter-remove">×</button>
                  </span>
                )}
                {purchaseSupplierFilter && (
                  <span className="purchase-filter-tag">
                    {suppliers.find(s => s.id == purchaseSupplierFilter)?.name || purchaseSupplierFilter}
                    <button onClick={() => handlePurchaseSupplierFilterChange('')} className="purchase-filter-remove">×</button>
                  </span>
                )}
                <span className="purchase-separator">•</span>
              </div>
            )}
            <span className="purchase-results-count">
              {purchaseTotalCount} compra{purchaseTotalCount !== 1 ? 's' : ''} encontrada{purchaseTotalCount !== 1 ? 's' : ''}
            </span>
          </div>
          
          {/* Purchase Pagination Controls */}
          {purchases.length > 0 && purchaseTotalPages > 1 && (
            <div className="purchase-pagination-section">
              <span className="purchase-pagination-info">
                Página {purchasePage} de {purchaseTotalPages}
              </span>
              <div className="purchase-pagination-controls">
                <button 
                  className="purchase-pagination-btn"
                  onClick={() => handlePurchasePageChange(1)}
                  disabled={purchasePage === 1}
                  title="Primera página"
                >
                  «
                </button>
                <button 
                  className="purchase-pagination-btn"
                  onClick={() => handlePurchasePageChange(purchasePage - 1)}
                  disabled={purchasePage === 1}
                  title="Página anterior"
                >
                  ‹
                </button>
                <span className="purchase-pagination-pages">
                  {Array.from({ length: Math.min(5, purchaseTotalPages) }, (_, i) => {
                    let pageNum;
                    if (purchaseTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (purchasePage <= 3) {
                      pageNum = i + 1;
                    } else if (purchasePage >= purchaseTotalPages - 2) {
                      pageNum = purchaseTotalPages - 4 + i;
                    } else {
                      pageNum = purchasePage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`purchase-pagination-btn ${purchasePage === pageNum ? 'active' : ''}`}
                        onClick={() => handlePurchasePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </span>
                <button 
                  className="purchase-pagination-btn"
                  onClick={() => handlePurchasePageChange(purchasePage + 1)}
                  disabled={purchasePage === purchaseTotalPages}
                  title="Página siguiente"
                >
                  ›
                </button>
                <button 
                  className="purchase-pagination-btn"
                  onClick={() => handlePurchasePageChange(purchaseTotalPages)}
                  disabled={purchasePage === purchaseTotalPages}
                  title="Última página"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="purchase-table-section">
          <div className="purchase-table-container">
            {purchasesLoading ? (
              <div className="purchase-loading">
                <Loader size={24} className="spinning" />
                <span>Cargando compras...</span>
              </div>
            ) : purchases.length > 0 ? (
              <table className="purchase-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>PROVEEDOR</th>
                    <th>ORDEN</th>
                    <th>METODO</th>
                    <th>PAGO</th>
                    <th>PACTADO</th>
                    <th>ENTREGADO</th>
                    <th>TOTAL</th>
                    <th>PRODUCTOS</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.map(purchase => (
                    <tr key={purchase.id}>
                      <td className="purchase-order-number">{purchase.poNumber || `PO-${purchase.id}`}</td>
                      <td>{purchase.supplier?.name || 'N/A'}</td>
                      <td className="purchase-date">{formatPurchaseDate(purchase.orderDate)}</td>
                      <td className="purchase-method">{formatPaymentMethodName(purchase.paymentMethod?.name)}</td>
                      <td className="purchase-date">{calculatePaymentDate(purchase)}</td>
                      <td className="purchase-date">{formatPurchaseDate(purchase.expectedDelivery)}</td>
                      <td>
                        <span 
                          className={`purchase-status-badge purchase-status-${purchase.status === 'pending' ? 'pendiente' :
                           purchase.status === 'received' ? 'recibido' :
                           purchase.status === 'partial' ? 'parcial' :
                           purchase.status === 'cancelled' ? 'cancelado' : purchase.status} clickable`}
                          onClick={() => handleStatusBadgeClick(purchase)}
                          title="Click para cambiar estado"
                        >
                          {purchase.status === 'pending' ? 'Pendiente' :
                           purchase.status === 'received' ? 'Recibido' :
                           purchase.status === 'partial' ? 'Parcial' :
                           purchase.status === 'cancelled' ? 'Cancelado' : purchase.status}
                        </span>
                      </td>
                      <td className={`purchase-amount ${purchase.totalAmount > 1000 ? 'large' : ''}`}>${purchase.totalAmount?.toFixed(2) || '0.00'}</td>
                      <td>
                        {purchase.items && purchase.items.length > 0 
                          ? purchase.items.slice(0, 2).map(item => 
                              `${item.productName} (${item.quantityOrdered})`
                            ).join(', ') + (purchase.items.length > 2 ? '...' : '')
                          : 'Sin productos'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="purchase-no-data">
                <ShoppingCart size={48} />
                <h3>No hay compras registradas</h3>
                <p>Haz clic en "Nueva Compra" para crear tu primera orden de compra.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="orders-content">
      <div className="orders-header">
        <h2>Historial de Ventas</h2>
        <div className="orders-filters">
          <OrderDatePicker
            selectedDate={ordersDateFilter}
            onDateChange={handleDateFilterChange}
            availableDates={availableDates}
          />
          <select 
            className="filter-select"
            value={ordersStatusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            title="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            <option value="PRESUPUESTO">Factura X</option>
            <option value="approved">Aprobada</option>
            <option value="pending">Pendiente</option>
            <option value="error">Error</option>
            <option value="cancelled">Cancelada</option>
          </select>
          <button 
            className="refresh-button"
            onClick={() => fetchOrders(ordersPage)}
            disabled={ordersLoading}
            title="Actualizar pedidos"
          >
            <RefreshCw size={16} className={ordersLoading ? 'spinning' : ''} />
            Actualizar
          </button>
          <button 
            className="download-button"
            onClick={handleSalesDownloadClick}
            disabled={ordersLoading}
            title="Descargar historial de ventas"
          >
            <Download size={16} />
            Descargar Historial
          </button>
        </div>
      </div>
      
      {/* Combined Filters and Pagination Info */}
      <div className="orders-filters-pagination-combined">
        <div className="filters-and-results">
          {(ordersDateFilter || ordersStatusFilter) && (
            <div className="active-filters">
              <span className="filters-label">Filtros activos:</span>
              {ordersDateFilter && (
                <span className="filter-tag">
                  {ordersDateFilter}
                  <button onClick={() => handleDateFilterChange('')} className="filter-remove">×</button>
                </span>
              )}
              {ordersStatusFilter && (
                <span className="filter-tag">
                  {ordersStatusFilter}
                  <button onClick={() => handleStatusFilterChange('')} className="filter-remove">×</button>
                </span>
              )}
              <span className="separator">•</span>
            </div>
          )}
          <span className="results-count">
            {ordersTotalCount} factura{ordersTotalCount !== 1 ? 's' : ''} encontrada{ordersTotalCount !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Pagination Controls */}
        {orders.length > 0 && ordersTotalPages > 1 && (
          <div className="pagination-section">
            <span className="pagination-info">
              Página {ordersPage} de {ordersTotalPages}
            </span>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={ordersPage === 1}
                title="Primera página"
              >
                «
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(ordersPage - 1)}
                disabled={ordersPage === 1}
                title="Página anterior"
              >
                ‹
              </button>
              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, ordersTotalPages) }, (_, i) => {
                  let pageNum;
                  if (ordersTotalPages <= 5) {
                    pageNum = i + 1;
                  } else if (ordersPage <= 3) {
                    pageNum = i + 1;
                  } else if (ordersPage >= ordersTotalPages - 2) {
                    pageNum = ordersTotalPages - 4 + i;
                  } else {
                    pageNum = ordersPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${ordersPage === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(ordersPage + 1)}
                disabled={ordersPage === ordersTotalPages}
                title="Página siguiente"
              >
                ›
              </button>
              <button 
                className="pagination-btn"
                onClick={() => handlePageChange(ordersTotalPages)}
                disabled={ordersPage === ordersTotalPages}
                title="Última página"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="orders-table-container">
        {ordersLoading ? (
          <div className="loading-state">
            <div>⏳ Cargando facturas...</div>
          </div>
        ) : orders.length > 0 ? (
          <>
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Tipo</th>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>Pago</th>
                  <th>Total</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="order-row"
                  >
                    {/* ESTADO: green Check if presupuesto_numero present, red X if not */}
                    <td>
                      {order.presupuesto_numero
                        ? <Check size={16} color="#27ae60" strokeWidth={2.5} />
                        : <X size={16} color="#e74c3c" strokeWidth={2.5} />}
                    </td>
                    {/* TIPO: tipo_comprobante as a badge */}
                    <td>
                      <span
                        className="invoice-status-badge"
                        style={{ background: '#3498db' }}
                      >
                        {order.tipo_comprobante === 'PRESUPUESTO' ? 'FACTURA X' : order.tipo_comprobante || '-'}
                      </span>
                    </td>
                    {/* NÉMERO: numero_comprobante ?? presupuesto_numero */}
                    <td className="order-number">
                      {order.numero_comprobante || order.presupuesto_numero || `#${order.id}`}
                    </td>
                    {/* CLIENTE */}
                    <td className="order-products">
                      {order.cliente_razon_social || 'Consumidor Final'}
                    </td>
                    {/* PAGO */}
                    <td>
                      <span
                        className="invoice-status-badge"
                        style={{ background: '#7f8c8d' }}
                      >
                        {getPaymentMethodLabel(order.payment_method)}
                      </span>
                    </td>
                    {/* TOTAL */}
                    <td className="order-total">
                      {order.total != null
                        ? `$${Number(order.total).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                        : '-'}
                    </td>
                    {/* FECHA: HH:MM DD/MM/YY */}
                    <td className="order-date">
                      {formatOrderDate(order.fecha_emision, order.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="empty-orders-state">
            <ShoppingCart size={48} />
            <h3>No hay facturas</h3>
            <p>
              {(ordersDateFilter || ordersStatusFilter) 
                ? 'No se encontraron facturas con los filtros aplicados.'
                : 'Las facturas emitidas aparecerán aquí.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Payment Methods Modal Component
  const PaymentMethodsModal = () => {
    if (!showPaymentMethodsModal) return null;

    return (
      <div className="payment-methods-modal-overlay" onClick={() => setShowPaymentMethodsModal(false)}>
        <div className="payment-methods-modal" onClick={(e) => e.stopPropagation()}>
          <div className="payment-methods-modal-header">
            <div className="modal-title-section">
              <CreditCard size={24} />
              <h2>Análisis de Métodos de Pago</h2>
            </div>
            <div className="modal-header-controls">
              <div className="period-tabs">
                <button 
                  className={`period-tab ${paymentMethodsPeriod === 'today' ? 'active' : ''}`}
                  onClick={() => {
                    setPaymentMethodsPeriod('today');
                    fetchPaymentMethodsDetails();
                  }}
                >
                  Hoy
                </button>
                <button 
                  className={`period-tab ${paymentMethodsPeriod === 'week' ? 'active' : ''}`}
                  onClick={() => {
                    setPaymentMethodsPeriod('week');
                    fetchPaymentMethodsDetails();
                  }}
                >
                  Semana
                </button>
                <button 
                  className={`period-tab ${paymentMethodsPeriod === 'month' ? 'active' : ''}`}
                  onClick={() => {
                    setPaymentMethodsPeriod('month');
                    fetchPaymentMethodsDetails();
                  }}
                >
                  Mes
                </button>
              </div>
              <button 
                className="close-modal-btn"
                onClick={() => setShowPaymentMethodsModal(false)}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="payment-methods-modal-content">
            {paymentMethodsModalLoading ? (
              <div className="modal-loading">
                <Loader size={32} className="spinning" />
                <span>Cargando datos de métodos de pago...</span>
              </div>
            ) : paymentMethodsModalData.methods ? (
              <>
                {/* Summary Stats and Donut Chart Layout */}
                <div className="payment-methods-overview">
                  {/* Left Column - Summary Stats */}
                  <div className="payment-methods-summary-column">
                    <div className="summary-stat">
                      <div className="summary-stat-label">Total Recaudado</div>
                      <div className="summary-stat-value">{formatCurrency(paymentMethodsModalData.totalRevenue)}</div>
                    </div>
                    <div className="summary-stat">
                      <div className="summary-stat-label">Total Transacciones</div>
                      <div className="summary-stat-value">{paymentMethodsModalData.totalTransactions}</div>
                    </div>
                    <div className="summary-stat">
                      <div className="summary-stat-label">Ticket Promedio</div>
                      <div className="summary-stat-value">
                        {formatCurrency(paymentMethodsModalData.totalTransactions > 0 
                          ? paymentMethodsModalData.totalRevenue / paymentMethodsModalData.totalTransactions 
                          : 0)}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Donut Chart */}
                  <div className="payment-methods-donut-section">
                    <div className="donut-chart-wrapper">
                      <svg viewBox="0 0 200 200" className="payment-donut-chart">
                        {/* Background circle */}
                        <circle
                          cx="100"
                          cy="100"
                          r="70"
                          fill="none"
                          stroke="#f1f5f9"
                          strokeWidth="30"
                        />
                        {/* Colored segments */}
                        {(() => {
                          if (!paymentMethodsModalData.methods || paymentMethodsModalData.methods.length === 0) return null;
                          
                          const circumference = 2 * Math.PI * 70;
                          let accumulatedOffset = 0;
                          
                          return paymentMethodsModalData.methods.map((method, index) => {
                            const segmentLength = (method.percentage / 100) * circumference;
                            const currentOffset = accumulatedOffset;
                            
                            accumulatedOffset += segmentLength;
                            
                            return (
                              <circle
                                key={method.originalMethod}
                                cx="100"
                                cy="100"
                                r="70"
                                fill="none"
                                stroke={method.color}
                                strokeWidth="30"
                                strokeDasharray={`${segmentLength} ${circumference}`}
                                strokeDashoffset={-currentOffset}
                                className="donut-segment"
                                transform="rotate(-90 100 100)"
                                style={{
                                  transition: 'all 0.8s ease'
                                }}
                              />
                            );
                          });
                        })()}
                        {/* Center text */}
                        <text
                          x="100"
                          y="105"
                          textAnchor="middle"
                          fontSize="14"
                          fontWeight="600"
                          fill="#64748b"
                        >
                          {paymentMethodsModalData.totalTransactions} pedidos
                        </text>
                      </svg>
                    </div>

                    {/* Compact Legend */}
                    <div className="payment-compact-legend">
                      {paymentMethodsModalData.methods.map((method) => (
                        <div key={method.originalMethod} className="compact-legend-item">
                          <div className="compact-legend-color" style={{ backgroundColor: method.color }} />
                          <span className="compact-legend-name">{method.method}</span>
                          <span className="compact-legend-percentage">{method.percentage.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Detailed Payment Methods Table */}
                <div className="payment-methods-table-container">
                  <table className="payment-methods-table">
                    <thead>
                      <tr>
                        <th>Método de Pago</th>
                        <th>Transacciones</th>
                        <th>Total Recaudado</th>
                        <th>% del Total</th>
                        <th>Ticket Promedio</th>
                        <th>Tendencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentMethodsModalData.methods.map((method, index) => (
                        <tr key={method.originalMethod}>
                          <td className="payment-method-name-cell">
                            <div className="payment-method-name-container">
                              <span 
                                className="payment-color-indicator" 
                                style={{ backgroundColor: method.color }}
                              />
                              <span className="payment-method-name">{method.method}</span>
                            </div>
                          </td>
                          <td className="payment-count-cell">
                            <span className="payment-count-number">{method.count}</span>
                            <span className="payment-count-label">transacciones</span>
                          </td>
                          <td className="payment-amount-cell">
                            <span className="payment-amount-value">{formatCurrency(method.value)}</span>
                          </td>
                          <td className="payment-percentage-cell">
                            <div className="percentage-container">
                              <span className="percentage-text">{method.percentage.toFixed(1)}%</span>
                              <div className="percentage-bar">
                                <div 
                                  className="percentage-fill"
                                  style={{ 
                                    width: `${method.percentage}%`,
                                    backgroundColor: method.color
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="payment-avg-cell">
                            {formatCurrency(method.avgTransaction)}
                          </td>
                          <td className="payment-trend-cell">
                            <div className="trend-indicator positive">
                              <ArrowUpRight size={16} />
                              <span>+5.2%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Visual Chart */}
                <div className="payment-methods-chart-section">
                  <h3>Distribución Visual</h3>
                  <div className="payment-methods-donut-container">
                    {/* Donut Chart */}
                    <div className="donut-chart-wrapper">
                      <svg viewBox="0 0 200 200" className="payment-donut-chart">
                        {/* Background circle */}
                        <circle
                          cx="100"
                          cy="100"
                          r="70"
                          fill="none"
                          stroke="#f1f5f9"
                          strokeWidth="30"
                        />
                        {/* Colored segments */}
                        {(() => {
                          if (!paymentMethodsModalData.methods || paymentMethodsModalData.methods.length === 0) return null;
                          
                          const circumference = 2 * Math.PI * 70;
                          let accumulatedOffset = 0;
                          
                          return paymentMethodsModalData.methods.map((method, index) => {
                            const segmentLength = (method.percentage / 100) * circumference;
                            const currentOffset = accumulatedOffset;
                            
                            accumulatedOffset += segmentLength;
                            
                            return (
                              <circle
                                key={method.originalMethod}
                                cx="100"
                                cy="100"
                                r="70"
                                fill="none"
                                stroke={method.color}
                                strokeWidth="30"
                                strokeDasharray={`${segmentLength} ${circumference}`}
                                strokeDashoffset={-currentOffset}
                                className="donut-segment"
                                transform="rotate(-90 100 100)"
                                style={{
                                  transition: 'all 0.8s ease'
                                }}
                              />
                            );
                          });
                        })()}
                        {/* Center text */}
                        <text
                          x="100"
                          y="90"
                          textAnchor="middle"
                          fontSize="18"
                          fontWeight="bold"
                          fill="#1e293b"
                        >
                          Total
                        </text>
                        <text
                          x="100"
                          y="110"
                          textAnchor="middle"
                          fontSize="24"
                          fontWeight="bold"
                          fill="#059669"
                        >
                          {formatCurrency(paymentMethodsModalData.totalRevenue)}
                        </text>
                      </svg>
                    </div>

                    {/* Legend */}
                    <div className="payment-donut-legend">
                      {paymentMethodsModalData.methods.map((method) => (
                        <div key={method.originalMethod} className="legend-item">
                          <div className="legend-color-dot" style={{ backgroundColor: method.color }} />
                          <div className="legend-content">
                            <div className="legend-name">{method.method}</div>
                            <div className="legend-stats">
                              <span className="legend-percentage">{method.percentage.toFixed(1)}%</span>
                              <span className="legend-amount">{formatCurrency(method.value)}</span>
                            </div>
                            <div className="legend-transactions">{method.count} transacciones</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="modal-no-data">
                <CreditCard size={48} />
                <h3>No hay datos disponibles</h3>
                <p>No se encontraron datos de métodos de pago para el período seleccionado.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-overlay">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Panel de Control</h1>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {isLoading && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            <div style={{ marginBottom: '10px' }}>⏳ Cargando datos...</div>
          </div>
        )}

        {error && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            background: error.includes('sin conexión') ? '#f39c12' : '#e74c3c',
            color: 'white',
            margin: '10px 20px',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {error.includes('sin conexión') ? '📡' : '⚠️'} {error}
            </div>
            {!error.includes('sin conexión') && (
              <button 
                onClick={fetchDashboardData}
                style={{ 
                  marginTop: '15px', 
                  padding: '8px 16px', 
                  background: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  border: '1px solid rgba(255,255,255,0.3)', 
                  borderRadius: '6px', 
                  cursor: 'pointer' 
                }}
              >
                Reintentar
              </button>
            )}
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="dashboard-tabs">
              <button 
                className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <div className="tab-icon-container">
                  <ChartColumn size={26} />
                  <CircleGauge size={18} className="mini-icon" />
                </div>
                Indicadores
              </button>
          <button 
            className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <div className="tab-icon-container">
              <ShoppingCart size={26} />
              <History size={18} className="mini-icon" />
            </div>
            Historial de Ventas
          </button>
          <button 
            className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <div className="tab-icon-container">
              <ClipboardList size={26} />
              <Package size={18} className="mini-icon" />
            </div>
            Inventario
          </button>
          <button 
            className={`tab-button ${activeTab === 'purchases' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchases')}
          >
            <div className="tab-icon-container">
              <Truck size={26} />
              <ClipboardPlus size={18} className="mini-icon" />
            </div>
            Compras
          </button>
          <button 
            className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <div className="tab-icon-container">
              <FileAxis3d size={26} />
              <BarChart3 size={18} className="mini-icon" />
            </div>
            Reportes
          </button>
        </div>

        <div className="dashboard-body">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'reports' && renderReports()}
          {activeTab === 'products' && renderProducts()}
          {activeTab === 'orders' && renderOrders()}
          {activeTab === 'purchases' && renderPurchases()}
        </div>
          </>
        )}

        {/* Purchase Management Modals */}
        <SupplierModal
          isOpen={showSupplierModal}
          onClose={closeSupplierModal}
          onSave={handleSaveSupplier}
          supplier={selectedSupplier}
          isLoading={supplierModalLoading}
        />

        <PurchaseModal
          isOpen={showPurchaseModal}
          onClose={closePurchaseModal}
          onSave={handleSavePurchase}
          suppliers={suppliers}
          purchase={selectedPurchase}
          isLoading={purchaseModalLoading}
        />

        {/* Status Update Modal */}
        {showStatusModal && (
          <div className="modal-overlay" onClick={closeStatusModal}>
            <div className="modal status-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Actualizar Estado de Compra</h3>
                <button onClick={closeStatusModal} className="modal-close">&times;</button>
              </div>
              <div className="modal-content">
                <div className="purchase-info">
                  <p><strong>Compra #{selectedPurchaseForStatus?.numeroOrden || selectedPurchaseForStatus?.poNumber}</strong></p>
                  <p><strong>Proveedor:</strong> {selectedPurchaseForStatus?.supplier?.name}</p>
                  <p><strong>Total:</strong> ${(selectedPurchaseForStatus?.totalAmount || 0).toLocaleString('es-CO')}</p>
                </div>
                
                <div className="status-options">
                  <h4>Seleccionar nuevo estado:</h4>
                  <div className="status-buttons">
                    <button
                      className={`status-btn status-pendiente ${selectedStatus === 'PENDIENTE' ? 'selected' : ''}`}
                      onClick={() => setSelectedStatus('PENDIENTE')}
                    >
                      PENDIENTE
                    </button>
                    <button
                      className={`status-btn status-parcial ${selectedStatus === 'PARCIAL' ? 'selected' : ''}`}
                      onClick={() => setSelectedStatus('PARCIAL')}
                    >
                      PARCIAL
                    </button>
                    <button
                      className={`status-btn status-completo ${selectedStatus === 'COMPLETO' ? 'selected' : ''}`}
                      onClick={() => setSelectedStatus('COMPLETO')}
                    >
                      COMPLETO
                    </button>
                    <button
                      className={`status-btn status-cancelado ${selectedStatus === 'CANCELADO' ? 'selected' : ''}`}
                      onClick={() => setSelectedStatus('CANCELADO')}
                    >
                      CANCELADO
                    </button>
                  </div>
                </div>

                <div className="note-section">
                  <label htmlFor="statusNote">
                    <strong>Nota requerida:</strong>
                  </label>
                  <textarea
                    id="statusNote"
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder={
                      selectedStatus === 'PARCIAL' ? "Explique el motivo del estado parcial..." :
                      selectedStatus === 'CANCELADO' ? "Explique el motivo de la cancelación..." :
                      "Seleccione PARCIAL o CANCELADO para agregar una nota"
                    }
                    rows="3"
                    disabled={selectedStatus !== 'PARCIAL' && selectedStatus !== 'CANCELADO'}
                    required={selectedStatus === 'PARCIAL' || selectedStatus === 'CANCELADO'}
                  />
                </div>

                <div className="modal-actions">
                  <button onClick={closeStatusModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleStatusUpdate}
                    className="btn-primary"
                    disabled={statusModalLoading || !selectedStatus || ((selectedStatus === 'PARCIAL' || selectedStatus === 'CANCELADO') && !statusNote.trim())}
                  >
                    {statusModalLoading ? 'Actualizando...' : 'Actualizar Estado'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Purchase Download Modal */}
        {showDownloadModal && (
          <div className="modal-overlay" onClick={closeDownloadModal}>
            <div className="modal download-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Descargar Historial de Compras</h3>
                <button onClick={closeDownloadModal} className="modal-close">&times;</button>
              </div>
              <div className="modal-content">
                <div className="download-options">
                  <div className="download-option-group">
                    <label htmlFor="downloadSupplier">
                      <strong>Filtrar por Proveedor:</strong>
                    </label>
                    <select
                      id="downloadSupplier"
                      value={downloadSupplier}
                      onChange={handleDownloadSupplierChange}
                      className="download-select"
                      disabled={availabilityLoading}
                    >
                      <option value="">TODOS - Todos los proveedores</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                    {availabilityLoading && <span className="loading-text">Verificando disponibilidad...</span>}
                  </div>

                  <div className="download-option-group">
                    <label>
                      <strong>Rango de Fechas:</strong>
                    </label>
                    <div className="date-range-options">
                      {(() => {
                        const availabilityToCheck = downloadSupplier === '' ? 
                          dateRangeAvailability.all : 
                          dateRangeAvailability.bySupplier[downloadSupplier]?.periods || {};
                        
                        return (
                          <>
                            <label className={`radio-option ${!availabilityToCheck.day ? 'disabled' : ''}`}>
                              <input
                                type="radio"
                                name="dateRange"
                                value="day"
                                checked={downloadDateRange === 'day'}
                                onChange={(e) => setDownloadDateRange(e.target.value)}
                                disabled={!availabilityToCheck.day || availabilityLoading}
                              />
                              <span>Hoy</span>
                              {!availabilityToCheck.day && <span className="unavailable-text">(No hay POs)</span>}
                            </label>
                            <label className={`radio-option ${!availabilityToCheck.week ? 'disabled' : ''}`}>
                              <input
                                type="radio"
                                name="dateRange"
                                value="week"
                                checked={downloadDateRange === 'week'}
                                onChange={(e) => setDownloadDateRange(e.target.value)}
                                disabled={!availabilityToCheck.week || availabilityLoading}
                              />
                              <span>Esta Semana</span>
                              {!availabilityToCheck.week && <span className="unavailable-text">(No hay POs)</span>}
                            </label>
                            <label className={`radio-option ${!availabilityToCheck.month ? 'disabled' : ''}`}>
                              <input
                                type="radio"
                                name="dateRange"
                                value="month"
                                checked={downloadDateRange === 'month'}
                                onChange={(e) => setDownloadDateRange(e.target.value)}
                                disabled={!availabilityToCheck.month || availabilityLoading}
                              />
                              <span>Este Mes</span>
                              {!availabilityToCheck.month && <span className="unavailable-text">(No hay POs)</span>}
                            </label>
                            <label className={`radio-option ${!availabilityToCheck.threemonths ? 'disabled' : ''}`}>
                              <input
                                type="radio"
                                name="dateRange"
                                value="threemonths"
                                checked={downloadDateRange === 'threemonths'}
                                onChange={(e) => setDownloadDateRange(e.target.value)}
                                disabled={!availabilityToCheck.threemonths || availabilityLoading}
                              />
                              <span>Últimos 3 Meses</span>
                              {!availabilityToCheck.threemonths && <span className="unavailable-text">(No hay POs)</span>}
                            </label>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="download-info">
                    <p><strong>El archivo incluirá:</strong></p>
                    <ul>
                      <li>PO Number, Proveedor, Fechas</li>
                      <li>Estado de entrega con fechas reales</li>
                      <li>Método de pago y totales</li>
                      <li>Lista de productos por orden</li>
                      <li>Notas y información de contacto</li>
                    </ul>
                  </div>
                </div>

                <div className="modal-actions">
                  <button onClick={closeDownloadModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleDownloadExcel}
                    className="btn-primary"
                    disabled={downloadLoading}
                  >
                    {downloadLoading ? 'Descargando...' : 'Descargar Excel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Download Modal */}
        {showSalesDownloadModal && (
          <div className="modal-overlay" onClick={closeSalesDownloadModal}>
            <div className="modal download-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Descargar Historial de Ventas</h3>
                <button onClick={closeSalesDownloadModal} className="modal-close">&times;</button>
              </div>
              <div className="modal-content">
                <div className="download-options">
                  <div className="download-option-group">
                    <label>
                      <strong>Rango de Fechas:</strong>
                    </label>
                    <div className="date-range-options">
                      <label className={`radio-option ${!salesAvailability.day ? 'disabled' : ''}`}>
                        <input
                          type="radio"
                          name="salesDateRange"
                          value="day"
                          checked={salesDownloadDateRange === 'day'}
                          onChange={(e) => setSalesDownloadDateRange(e.target.value)}
                          disabled={!salesAvailability.day || salesAvailabilityLoading}
                        />
                        <span>Hoy</span>
                        {!salesAvailability.day && <span className="unavailable-text">(No hay pedidos)</span>}
                      </label>
                      <label className={`radio-option ${!salesAvailability.week ? 'disabled' : ''}`}>
                        <input
                          type="radio"
                          name="salesDateRange"
                          value="week"
                          checked={salesDownloadDateRange === 'week'}
                          onChange={(e) => setSalesDownloadDateRange(e.target.value)}
                          disabled={!salesAvailability.week || salesAvailabilityLoading}
                        />
                        <span>Esta Semana</span>
                        {!salesAvailability.week && <span className="unavailable-text">(No hay pedidos)</span>}
                      </label>
                      <label className={`radio-option ${!salesAvailability.month ? 'disabled' : ''}`}>
                        <input
                          type="radio"
                          name="salesDateRange"
                          value="month"
                          checked={salesDownloadDateRange === 'month'}
                          onChange={(e) => setSalesDownloadDateRange(e.target.value)}
                          disabled={!salesAvailability.month || salesAvailabilityLoading}
                        />
                        <span>Este Mes</span>
                        {!salesAvailability.month && <span className="unavailable-text">(No hay pedidos)</span>}
                      </label>
                      <label className={`radio-option ${!salesAvailability.threemonths ? 'disabled' : ''}`}>
                        <input
                          type="radio"
                          name="salesDateRange"
                          value="threemonths"
                          checked={salesDownloadDateRange === 'threemonths'}
                          onChange={(e) => setSalesDownloadDateRange(e.target.value)}
                          disabled={!salesAvailability.threemonths || salesAvailabilityLoading}
                        />
                        <span>Últimos 3 Meses</span>
                        {!salesAvailability.threemonths && <span className="unavailable-text">(No hay pedidos)</span>}
                      </label>
                    </div>
                    {salesAvailabilityLoading && <span className="loading-text">Verificando disponibilidad...</span>}
                  </div>

                  <div className="download-info">
                    <p><strong>El archivo incluirá:</strong></p>
                    <ul>
                      <li>Estado, Número de Pedido, Fecha</li>
                      <li>Lista de productos por orden</li>
                      <li>Método de pago y totales</li>
                      <li>Información completa de ventas</li>
                    </ul>
                  </div>
                </div>

                <div className="modal-actions">
                  <button onClick={closeSalesDownloadModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleSalesDownloadExcel}
                    className="btn-primary"
                    disabled={salesDownloadLoading}
                  >
                    {salesDownloadLoading ? 'Descargando...' : 'Descargar Excel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification System */}
        <NotificationSystem />

        {/* Payment Methods Modal */}
        <PaymentMethodsModal />
      </div>
    </div>
  );
};

export default Dashboard;
